
import * as THREE from "../../libs/three.js/build/three.module.js";
import {CURSOR_MARKER_COLOR} from "./CursorPointMarker.js";
import {showGroupedContextMenu} from "./VertexContextMenu.js";
import {createDialogOverlay, showGeometryWarning, formatLength, fillDialog, pvpButton} from "./DialogHelpers.js";
import {hostDialogAvailable, requestHostToolPrompt, htmlMessageToText} from "./HostDialog.js";
import {runEditCircle, runDissolveCircle} from "./tools/circleTool.js";
import {runEditRectangle, runDissolveRectangle} from "./tools/rectangleTool.js";
import {settings} from "./settings/DigitizationSettings.js";
import {createDashedLine, createMarker, setLinePositions} from "./tools/perpendicularCapture.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "./ScreenSpaceScale.js";
import {getEditingCapabilities, resolveFixedAngleVertexIndex} from "./EditingCapabilities.js";
import {PipeVisual} from "./PipeVisual.js";
import {DigitizationTools} from "./ToolRegistry.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {createFeature} from "./Feature.js";
import {loadCrossSectionStations, renderAndShowCrossSectionReport} from "./tools/crossSectionTool.js";
import {showCrossSectionErrorPanel} from "./crosssection/CrossSectionReportPanel.js";
import {isValidPolygonRing} from "./geometry/polygonValidity.js";
import {
	straightenLine2D,
	straightenLine3D,
	simplifyPolyline,
	orthogonalizePolyline,
	simplifyClosedRing,
	orthogonalizeClosedRing,
	computeFixedAngleVertex,
	projectPointToFixedAngleArc,
	alignSegmentParallel,
	alignSegmentPerpendicular,
	computeSegmentAlignment,
	insertPointOnSegment,
	reverseDirection,
	movePoint,
	distributePointsEvenly,
	movePointInZ,
	movePointsInZ,
	setAbsoluteZ,
	extendEndpointByDistance,
	dist2D,
} from "./geometry/vectorEditing.js";
import {determineArcPlane, sampleArcPolyline, CURVE_SAMPLING_TOLERANCE_M} from "./geometry/arcFromSagitta.js";
import {interpolateZBetween, setSlopeBetween} from "./geometry/zProfile.js";
import {getArcRanges, findArcRangeForSegment, removeArcRange, resampleArcRange} from "./geometry/arcRangeStore.js";
import {
	splitPolylineAt,
	mergePolylines,
	extendPolylineToReference,
	trimPolylineToReference,
	trimPolylineAndBridge,
	connectEndpoints,
	intersectAndInsert,
} from "./geometry/lineSplit.js";
import {DEFAULT_PLANAR_TOLERANCE_M} from "./geometry/lineIntersection2D.js";
import {isValidSnapTarget, snapMoveToTarget, planSnapSelectionToTarget} from "./geometry/snapToPoint.js";
import {planVertexMenu, planSegmentMenu, planRangeMenu, planFeatureMenu, bindPlan} from "./menuPlan.js";

import {t, numberLocale} from "./i18n/index.js";
import {buildMoveZTabs} from "./moveZTabs.js";
// Platform-wide context-menu revision: which "kind" of object a Shift+
// Right-click ultimately concerns, independent of how it's implemented
// under the hood - see EditingCapabilities.js's own header comment for the
// full model. Only openFeatureMenu() (the whole-object menu) needs this;
// vertex/segment/range menus only ever apply to ordinary FeatureVisual
// geometry (a Tværprofil's underlying path IS an editable LineString for
// those purposes - see crossSectionTool.js's own doc comment - and a Rør
// has no vertex/segment concept to reach those menus with at all).
function resolveObjectKind(dm, visual){
	if(visual instanceof PipeVisual) return "pipe";
	const feature = dm.getFeature(visual);
	if(feature && feature.properties.tool === "digitize_cross_sections") return "crossprofile";
	// Måling med fast antal punkter (Mål vinkel/cirkel/azimut - værktøjets
	// `fixedVertexCount`): kun flyt/snap, aldrig indsæt/slet/del punkter.
	const tool = feature ? DigitizationTools.get(feature.properties.tool) : null;
	if(tool && tool.fixedVertexCount) return "measure-fixed";
	return visual.geometryType ? visual.geometryType.toLowerCase() : null;
}

// Context titles (Punkt/Linje/Polygon/Tværprofil/Rør) now live in menuPlan.js
// alongside the rest of the pure menu structure.

// Shared with perpendicularCapture.js/parallelOffsetTool.js's own live
// preview - see PREVIEW_COLOR there.
const PREVIEW_COLOR = 0xffdc00;

// Interactive glue for "Vektoroperationer" - the vertex-, segment- and
// feature-level Shift+Right-click menus (see DigitizationModule's mouseup
// listener, which resolves a click to exactly one of vertex/segment/feature
// via _findHoveredVertex()/_findHoveredSegment()/selectedVisual and calls
// the matching open*Menu() below). Which operations a given menu actually
// contains is decided entirely by EditingCapabilities.js's pure capability
// model - an operation that doesn't apply is never added, not shown
// disabled. The actual geometry lives entirely in geometry/vectorEditing.js
// (pure, unit-tested); everything here is: build the menu from capabilities,
// read/apply the two "Vektorredigering" settings, run the small pre-op
// popups (Fast vinkel's angle, reference-segment picking), and always
// commit through DigitizationModule.applyFeatureGeometry() - the one place
// that keeps Feature/Properties/Registreringer/snap-targets in sync.
//
// Architecture is deliberately left open for the task's own deferred
// operations (Opdel, Saml, Forlæng, Trim - section 18): each of those would
// add one more geometry/vectorEditing.js function plus one more menu item
// here, following exactly the same "compute -> check maxSagitta -> apply or
// warn" shape every operation below already uses - no redesign needed.

// Selection/highlight audit finding: this used to equal FeatureVisual's own
// COLOR_SELECTED (0xffa500) - meaning a segment/range highlight on an
// ALREADY-selected feature was visually indistinguishable from every other
// segment of that same feature (both painted the identical orange). Now
// matches this file's own PREVIEW_COLOR (0xffdc00, "the current operation's
// target") and FeatureVisual.js's COLOR_HIGHLIGHT_HEX - one consistent
// highlight colour, always distinct from both COLOR_DEFAULT (blue) and
// COLOR_SELECTED (orange), regardless of whether the feature happens to
// already be selected.
const COLOR_HIGHLIGHT = 0xffdc00;

function getMaxSagitta(){
	return settings.get("vectorEditing.maxSagitta") || 0.02;
}

function getAngleTolerance(){
	return settings.get("vectorEditing.angleTolerance") || 5;
}

// The intersection-based tools (Forlæng/Trim×2/Sæt punkt ved skæring) are all
// plan operations - they read only this ONE planar tolerance from the
// "topology" settings group (see SettingsSchema.js). They no longer gate on a
// vertical tolerance (stabilisation task §4 / §6 / §7).
function getPlanarTolerance(){
	return settings.get("topology.planarTolerance") || DEFAULT_PLANAR_TOLERANCE_M;
}

function toPlainPoints(visual){
	return visual.points.map((p) => ({x: p.position.x, y: p.position.y, z: p.position.z}));
}

function toVector3Array(points){
	return points.map((p) => new THREE.Vector3(p.x, p.y, p.z));
}

// createDialogOverlay()/showGeometryWarning()/formatLength() moved to
// DialogHelpers.js (own doc comment there) - factored out so the
// parametric-construction tool files (tools/circleTool.js etc.) can reuse
// them without a circular import back into this file.

function refuseOrWarn(dm, operationTitle, maxDeviation, maxSagitta){
	showGeometryWarning(
		dm,
		operationTitle,
		`${t("Operationen blev ikke gennemført.")}<br><br>` +
		`${t("Beregnet maksimal afvigelse:")} <b>${formatLength(maxDeviation)}</b><br>` +
		`${t("Tilladt maksimal pilhøjde:")} <b>${formatLength(maxSagitta)}</b><br><br>` +
		t(`Øg "Maksimal pilhøjde" under Indstillinger → Vektorredigering, hvis resultatet er acceptabelt.`),
	);
}

// Vinkel [___]  [45°] [90°]  Annuller/Fortsæt - same modal shape as
// DistancePrompt.js, with two quick-choice buttons added (section 8).
function promptFixedAngle(dm, defaultValue = 90){
	// Vært-UI (PointViews frontend) viser dialogen; uden vært (standalone Potree) bruges DOM-dialogen herunder.
	if(hostDialogAvailable(dm.viewer)){
		return requestHostToolPrompt(dm.viewer, {
			kind: 'form',
			size: 's',
			title: t('Fast vinkel'),
			text: t('Tilpasser de to valgte segmenter til den angivne vinkel med mindst mulig geometrisk ændring.'),
			fields: [{id: 'angle', type: 'number', label: t('Vinkel [°]'), value: String(defaultValue), step: 'any',
				quickValues: [{label: '45°', value: '45'}, {label: '90°', value: '90'}]}],
			confirmLabel: t('Fortsæt'),
		}).then((result) => {
			if(result.action !== 'confirm') return undefined;
			const num = parseFloat(result.values.angle);
			return isFinite(num) && num > 0 && num < 180 ? num : undefined;
		});
	}
	return new Promise((resolve) => {
		const {overlay, box} = createDialogOverlay(dm, 260);

		fillDialog(box, {
			title: t(`Fast vinkel`),
			description: t(`Tilpasser de to valgte segmenter til den angivne vinkel med mindst mulig geometrisk ændring.`),
			body: `<label class="pvp-label">${t("Vinkel [°]")}</label>
				<input class="pvp-input" type="number" step="any" min="0" max="180" value="${defaultValue}"
				 />
				<div class="pvp-row">
					<button type="button" class="pvp-chip" data-quick="45">45°</button>
					<button type="button" class="pvp-chip" data-quick="90">90°</button>
				</div>`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('continue', t("Fortsæt"), 'primary'),
		});

		const input = box.querySelector('input');
		input.focus();
		input.select();

		let done = false;
		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};
		const finish = (value) => {
			if(done) return;
			done = true;
			cleanup();
			resolve(value);
		};

		const submit = () => {
			const num = parseFloat(input.value);
			finish(isFinite(num) && num > 0 && num < 180 ? num : undefined);
		};

		const onCancelEvent = () => finish(undefined);

		box.querySelectorAll('[data-quick]').forEach((btn) => {
			btn.addEventListener('click', () => { input.value = btn.getAttribute('data-quick'); });
		});
		box.querySelector('[data-action="continue"]').addEventListener('click', submit);
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(undefined));
		box.addEventListener('keydown', (e) => {
			if(e.key === 'Escape'){ e.stopPropagation(); finish(undefined); }
			if(e.key === 'Enter'){ e.preventDefault(); submit(); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

// Afstand [___cm]  [-20][-10][-5][-2] [+2][+5][+10][+20]  Annuller/Fortsæt -
// same modal shape as promptFixedAngle()/DistancePrompt.js, shared by "Flyt
// punkt i Z" and "Forlæng endesegment" (task: "popup dialog hvor brugeren
// enten kan skrive eller vælge antal cm ... både positiv og negativ") - a
// quick-value button only FILLS the input (same convention as
// promptFixedAngle()'s 45°/90°), it never auto-submits, so a picked value can
// still be reviewed or hand-edited before confirming. Resolves the value in
// METRES (the unit every geometry function in this file works in), or
// `undefined` if the user cancelled or left an empty/zero/invalid field -
// zero has no effect on either operation, so it is treated as "nothing
// entered", exactly like DistancePrompt.js's own empty-field convention.
function promptSignedDistanceCm(dm, {title, description} = {}){
	const quickValuesCm = [-20, -10, -5, -2, 2, 5, 10, 20];
	if(hostDialogAvailable(dm.viewer)){
		return requestHostToolPrompt(dm.viewer, {
			kind: 'form',
			size: 's',
			title,
			text: htmlMessageToText(description || ''),
			fields: [{id: 'distanceCm', type: 'number', label: t('Afstand [cm]'), placeholder: t('fx -15 eller 15'), step: 'any',
				quickValues: quickValuesCm.map((v) => ({label: `${v > 0 ? '+' : ''}${v}`, value: String(v)}))}],
			confirmLabel: t('Fortsæt'),
		}).then((result) => {
			if(result.action !== 'confirm') return undefined;
			const cm = parseFloat(result.values.distanceCm);
			return isFinite(cm) && cm !== 0 ? cm / 100 : undefined;
		});
	}
	return new Promise((resolve) => {
		const {overlay, box} = createDialogOverlay(dm, 280);

		const quickButtonsHtml = quickValuesCm.map((v) =>
			`<button type="button" class="pvp-chip" data-quick="${v}">${v > 0 ? '+' : ''}${v}</button>`,
		).join('');

		fillDialog(box, {
			title: `${title}`,
			description: `${description || ''}`,
			body: `<label class="pvp-label">${t("Afstand [cm]")}</label>
				<input class="pvp-input" type="number" step="any" placeholder="${t("fx -15 eller 15")}"
				 />
				<div class="pvp-row">${quickButtonsHtml}</div>`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('continue', t("Fortsæt"), 'primary'),
		});

		const input = box.querySelector('input');
		input.focus();

		let done = false;
		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};
		const finish = (value) => {
			if(done) return;
			done = true;
			cleanup();
			resolve(value);
		};

		const submit = () => {
			const cm = parseFloat(input.value);
			finish(isFinite(cm) && cm !== 0 ? cm / 100 : undefined);
		};
		const onCancelEvent = () => finish(undefined);

		box.querySelectorAll('[data-quick]').forEach((btn) => {
			btn.addEventListener('click', () => { input.value = btn.getAttribute('data-quick'); input.focus(); });
		});
		box.querySelector('[data-action="continue"]').addEventListener('click', submit);
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(undefined));
		box.addEventListener('keydown', (e) => {
			if(e.key === 'Escape'){ e.stopPropagation(); finish(undefined); }
			if(e.key === 'Enter'){ e.preventDefault(); submit(); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

// "Flyt linje/segment/del i Z": tabs with distances and pipe dimensions (moveZTabs.js), a direction and an editable cm value, rendered by
// the host (kind 'offset'). Resolves METRES like promptSignedDistanceCm(), negative is down, `undefined` if cancelled. Without a host
// (standalone Potree) the plain signed-cm dialog is the fallback.
function promptMoveLineZ(dm, {title, description}){
	if(!hostDialogAvailable(dm.viewer)){
		return promptSignedDistanceCm(dm, {title, description: `${description} ${t("Positivt tal flytter op, negativt tal flytter ned.")}`});
	}
	return requestHostToolPrompt(dm.viewer, {
		kind: 'offset',
		size: 'm',
		title,
		text: description,
		tabs: buildMoveZTabs(),
		locale: numberLocale(),
		offsetLabel: t('Z-forskydning (cm)'),
		downLabel: t('Flyt ned'),
		upLabel: t('Flyt op'),
		wallInvalidMessage: t('Godstykkelsen skal være mindre end dimensionen.'),
		resetLabel: t('Nulstil'),
		confirmLabel: t('Flyt'),
	}).then((result) => {
		if(result.action !== 'confirm') return undefined;
		const cm = parseFloat(result.values.offsetCm);
		return isFinite(cm) && cm !== 0 ? cm / 100 : undefined;
	});
}

// Kote [___m]  Annuller/Fortsæt - "Sæt kote…" (task §11): an ABSOLUTE Z
// value (unlike promptSignedDistanceCm()'s relative Δz), so 0 is a perfectly
// valid input (no "empty=cancelled" convention on zero here) and there are
// no quick +/- buttons - a kote isn't naturally expressed as a small offset.
function promptAbsoluteZ(dm, {title, description, initialValue = ""} = {}){
	if(hostDialogAvailable(dm.viewer)){
		return requestHostToolPrompt(dm.viewer, {
			kind: 'form',
			size: 's',
			title,
			text: htmlMessageToText(description || ''),
			fields: [{id: 'z', type: 'number', label: t('Kote [m]'), value: String(initialValue), step: 'any'}],
			confirmLabel: t('Fortsæt'),
		}).then((result) => {
			if(result.action !== 'confirm') return undefined;
			const z = parseFloat(result.values.z);
			return isFinite(z) ? z : undefined;
		});
	}
	return new Promise((resolve) => {
		const {overlay, box} = createDialogOverlay(dm, 260);

		fillDialog(box, {
			title: `${title}`,
			description: `${description || ''}`,
			body: `<label class="pvp-label">${t("Kote [m]")}</label>
				<input class="pvp-input" type="number" step="any" value="${initialValue}"
				 />`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('continue', t("Fortsæt"), 'primary'),
		});

		const input = box.querySelector('input');
		input.focus();
		input.select();

		let done = false;
		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};
		const finish = (value) => {
			if(done) return;
			done = true;
			cleanup();
			resolve(value);
		};

		const submit = () => {
			const z = parseFloat(input.value);
			finish(isFinite(z) ? z : undefined);
		};
		const onCancelEvent = () => finish(undefined);

		box.querySelector('[data-action="continue"]').addEventListener('click', submit);
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(undefined));
		box.addEventListener('keydown', (e) => {
			if(e.key === 'Escape'){ e.stopPropagation(); finish(undefined); }
			if(e.key === 'Enter'){ e.preventDefault(); submit(); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

// ── operations ───────────────────────────────────────────────────────────

// range is either null (whole vector) or {start, end} (vertex indices, from
// "Vælg sammenhængende del…" - see startSegmentSelection()) - resolved to concrete
// [start,end] indices once here rather than repeating the same null-check
// in every operation below.
function resolveRange(range, points){
	return range ? [range.start, range.end] : [0, points.length - 1];
}

function runStraighten(dm, visual, mode, range){
	const points = toPlainPoints(visual);
	const [start, end] = resolveRange(range, points);
	const title = mode === "2D" ? "Ret ud i 2D" : "Ret ud i 3D";
	const result = mode === "2D" ? straightenLine2D(points, start, end) : straightenLine3D(points, start, end);

	const maxSagitta = getMaxSagitta();
	if(result.maxDeviation > maxSagitta){
		refuseOrWarn(dm, title, result.maxDeviation, maxSagitta);
		return;
	}
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

function runSimplify(dm, visual, range){
	const points = toPlainPoints(visual);
	const [start, end] = resolveRange(range, points);
	const result = simplifyPolyline(points, getMaxSagitta(), start, end);
	// simplifyPolyline is constructed to never exceed the given tolerance
	// (that tolerance IS maxSagitta here) - nothing to refuse, always safe
	// to apply directly
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

function runOrthogonalize(dm, visual, range){
	const points = toPlainPoints(visual);
	const [start, end] = resolveRange(range, points);
	const result = orthogonalizePolyline(points, getAngleTolerance(), getMaxSagitta(), start, end);
	// orthogonalizePolyline clamps its own per-vertex displacement to
	// maxSagitta internally - always safe to apply
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

function runReverse(dm, visual){
	dm.applyFeatureGeometry(visual, toVector3Array(reverseDirection(toPlainPoints(visual))));
}

// ── Polygon whole-object Forenkle/Ortogonaliser ─────────────────────────
//
// Both reuse the exact same tolerance-safe pure functions as the LineString
// versions above, via geometry/vectorEditing.js's closed-ring adapters
// (simplifyClosedRing()/orthogonalizeClosedRing()) - no separate algorithm.
// Unlike the LineString versions, a Polygon result is additionally
// validated afterwards (geometry/polygonValidity.js's isValidPolygonRing()):
// neither underlying algorithm is ring-aware, so - although both are
// individually tolerance-safe - the task's own "polygonvaliditet skal
// bevares" requirement means the result must still be checked before
// committing, same refuseOrWarn()-style pattern as every other operation
// here that can fail its own precondition.
function runSimplifyPolygon(dm, visual){
	const points = toPlainPoints(visual);
	const result = simplifyClosedRing(points, getMaxSagitta());
	if(!isValidPolygonRing(result.points)){
		showGeometryWarning(dm, t("Forenkle"), t("Operationen blev ikke gennemført, fordi resultatet ikke længere ville være en gyldig polygon (selvskæring eller for lille areal)."));
		return;
	}
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

function runOrthogonalizePolygon(dm, visual){
	const points = toPlainPoints(visual);
	const result = orthogonalizeClosedRing(points, getAngleTolerance(), getMaxSagitta());
	if(!isValidPolygonRing(result.points)){
		showGeometryWarning(dm, t("Ortogonaliser"), t("Operationen blev ikke gennemført, fordi resultatet ikke længere ville være en gyldig polygon (selvskæring eller for lille areal)."));
		return;
	}
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

// ── Tværprofil whole-object "Vis rapport" ───────────────────────────────
//
// Regenerates the report from the feature's CURRENT geometry (visual.
// rawCoordinates() - not whatever it was when the report was first shown) -
// this is what lets a user adjust the digitized path via the ordinary
// LineString editing operations above and simply re-open the report for an
// up-to-date result, rather than needing the path's own editing operations
// to be restricted to avoid a "stale report" risk (see EditingCapabilities.js's
// own header comment). Reuses loadCrossSectionStations()/
// renderAndShowCrossSectionReport() - the exact same pipeline the original
// capture flow uses (crossSectionTool.js) - never a parallel generator.
export async function showCrossSectionReportForFeature(dm, visual){
	const feature = dm.getFeature(visual);
	if(!feature) return;
	const crossSection = feature.properties.attributes && feature.properties.attributes.crossSection;
	if(!crossSection) return; // defensive - canShowReport is only ever granted for a real Tværprofiler feature, which always has this

	const pointcloud = dm.viewer.scene.pointclouds[0];
	if(!pointcloud){
		showGeometryWarning(dm, t("Vis rapport"), t("Ingen punktsky er indlæst."));
		return;
	}

	const points = toPlainPoints(visual).map((p) => [p.x, p.y, p.z]);

	dm._showInstruction(t("Tværprofilrapport"), t("Genererer rapport…"));
	const result = await loadCrossSectionStations(pointcloud, {
		points, interval: crossSection.interval, profileLength: crossSection.profileLength, band: crossSection.band,
		viewer: dm.viewer,
	});
	dm._hideToolHint();

	if(result.error){
		showCrossSectionErrorPanel(result.error, dm);
		return;
	}

	renderAndShowCrossSectionReport(dm, {
		points, profileLength: crossSection.profileLength, stations: result.stations,
		featureName: feature.properties.name, interval: crossSection.interval, band: crossSection.band,
		dataSource: pointcloud.name || null, coordinateSystem: pointcloud.projection || null,
	});
}

function runDistributeEvenly(dm, visual, range){
	const points = toPlainPoints(visual);
	const [start, end] = resolveRange(range, points);
	const result = distributePointsEvenly(points, start, end);
	// Not gated by Maksimal pilhøjde - see distributePointsEvenly()'s own
	// doc comment (same reasoning as Vend retning/Flyt punkt: this IS the
	// requested result, not an automated correction with a tolerance).
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

// ── Interpolér Z mellem punkter (range menu) ──────────────────────────────
//
// Both range endpoints stay fixed; every interior vertex's Z is linearly
// interpolated by its own horizontal station fraction (geometry/zProfile.js's
// interpolateZBetween() - see its own doc comment for why horizontal/XY
// station, not vertex index or 3D distance). Not gated by Maksimal
// pilhøjde - same "this IS the requested result" reasoning as
// runDistributeEvenly() above; the resulting Z change is an explicit,
// user-requested Z-profile, not an automated tolerance-bounded correction.
function runInterpolateZ(dm, visual, range){
	const points = toPlainPoints(visual);
	const [start, end] = resolveRange(range, points);
	const result = interpolateZBetween(points, start, end);
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

// ── Sæt fald/hældning (range menu) ────────────────────────────────────────
//
// promptSlope() collects the signed slope ratio (promille/procent converted
// to a plain rise/horizontal-run ratio) and which range endpoint is the
// kote-reference; geometry/zProfile.js's setSlopeBetween() does the actual
// per-vertex Z computation (see its own doc comment for the station/sign
// convention). Not gated by Maksimal pilhøjde - same reasoning as
// runInterpolateZ()/runDistributeEvenly() above.
async function runSetSlope(dm, visual, range){
	const points = toPlainPoints(visual);
	const [start, end] = resolveRange(range, points);

	const picked = await promptSlope(dm, {
		description: t("Beregner Z for de øvrige punkter i intervallet ud fra referencepunktets kote og den angivne hældning. X/Y ændres ikke."),
	});
	if(picked === undefined) return;

	const result = setSlopeBetween(points, start, end, {referenceEnd: picked.referenceEnd, slope: picked.ratio});
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

// Hældning [___]  ‰/%  Startpunkt/Slutpunkt  Vend retning  Annuller/Fortsæt.
// Resolves to {ratio, referenceEnd} (ratio already converted to a plain
// rise/horizontal-run number, per the chosen unit) or `undefined` on cancel.
// The reference-end buttons make explicit which end's kote is held fixed
// (task §13: "Det skal være tydeligt i UI hvilken ende der er reference");
// "Vend retning" only negates the typed value (same convention as the arc
// dialog's "Vend bue") - it deliberately does NOT touch which end is
// selected as reference, since geometry/zProfile.js's setSlopeBetween()
// already measures the slope in a FIXED direction independent of that
// choice (see its own doc comment) - so reversing direction is a genuine,
// separate action, never an accidental side-effect of picking the other end.
function promptSlope(dm, {title = t("Sæt fald/hældning"), description} = {}){
	return new Promise((resolve) => {
		const {overlay, box} = createDialogOverlay(dm, 300);

		fillDialog(box, {
			title: `${title}`,
			description: `${description || ''}`,
			body: `<label class="pvp-label">${t("Hældning")}</label>
				<div class="pvp-row">
					<input class="pvp-input" type="number" step="any" placeholder="${t("fx -5 eller 2.5")}"
					 />
					<select class="pvp-input" data-role="unit">
						<option value="promille">${t("‰ (promille)")}</option>
						<option value="procent">${t("% (procent)")}</option>
					</select>
				</div>
				<label class="pvp-label">${t("Referencepunkt (fastholdt kote)")}</label>
				<div class="pvp-row">
					<button type="button" class="pvp-chip" data-ref="start">${t("Startpunkt")}</button>
					<button type="button" class="pvp-chip" data-ref="end">${t("Slutpunkt")}</button>
				</div>`,
			actions: pvpButton('flip', t("Vend retning"), 'start') + pvpButton('cancel', t("Annuller")) + pvpButton('continue', t("Fortsæt"), 'primary'),
		});

		const input = box.querySelector('input');
		const unitSelect = box.querySelector('[data-role="unit"]');
		const refButtons = box.querySelectorAll('[data-ref]');
		let referenceEnd = "start";
		const highlightRef = () => refButtons.forEach((b) => {
			b.classList.toggle('is-active', b.getAttribute('data-ref') === referenceEnd);
		});
		highlightRef();
		refButtons.forEach((b) => b.addEventListener('click', () => {
			referenceEnd = b.getAttribute('data-ref');
			highlightRef();
		}));

		input.focus();

		let done = false;
		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};
		const finish = (value) => {
			if(done) return;
			done = true;
			cleanup();
			resolve(value);
		};

		const submit = () => {
			const value = parseFloat(input.value);
			if(!isFinite(value)){ finish(undefined); return; }
			const divisor = unitSelect.value === "procent" ? 100 : 1000;
			finish({ratio: value / divisor, referenceEnd});
		};
		const onCancelEvent = () => finish(undefined);

		box.querySelector('[data-action="flip"]').addEventListener('click', () => {
			const current = parseFloat(input.value);
			if(isFinite(current)) input.value = -current;
			input.focus();
		});
		box.querySelector('[data-action="continue"]').addEventListener('click', submit);
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(undefined));
		box.addEventListener('keydown', (e) => {
			if(e.key === 'Escape'){ e.stopPropagation(); finish(undefined); }
			if(e.key === 'Enter'){ e.preventDefault(); submit(); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

function runInsertPoint(dm, visual, hit){
	const points = toPlainPoints(visual);
	const result = insertPointOnSegment(points, hit.segmentIndex, hit.t);
	dm.applyFeatureGeometry(visual, toVector3Array(result));
}

// ── Flyt punkt (vertex menu) ─────────────────────────────────────────────
//
// Shift+Right-click a vertex -> "Flyt punkt" -> move the mouse in the point
// cloud (via the same _pickPlacementPosition() every other tool uses, so
// snapping to other features' endpoints comes along for free, task section
// 8) -> left-click commits, ESC cancels.
//
// Preview never touches the real FeatureVisual (its sphere/edges/labels)
// or the registered Feature - it's a separate overlay (one marker at the
// candidate position, one dashed line to each of the moved vertex's real,
// UNCHANGED neighbours), exactly the same "construction preview, real
// geometry untouched until commit" convention perpendicularCapture.js
// already uses for brand-new features (see PREVIEW_COLOR/createMarker/
// createDashedLine there). Since nothing is written anywhere until commit,
// ESC leaves the feature numerically identical to before by construction -
// there is nothing to undo, because nothing was ever applied.
//
// Preview and commit both compute through the same pure movePoint() - the
// mousemove handler recomputes it every frame purely to position the overlay;
// commit re-runs it once against the last picked position and applies the
// full resulting array through dm.applyFeatureGeometry(). Nothing is written
// until commit, so ESC leaves the feature numerically identical by
// construction.
//
// Acts on the SELECTED vertex only (stabilisation task §1.1 / §3): geometric
// coincidence with another feature's endpoint is NOT propagated here. If the
// move broke such a coincidence, DigitizationModule.checkVertexMoveConnectionWarning()
// surfaces that as a non-blocking toast afterwards - information, not scope.
export function runMovePoint(dm, visual, vertexIndex){
	const domElement = dm.viewer.renderer.domElement;
	const viewer = dm.viewer;

	const originalPoints = toPlainPoints(visual);
	const previousPosition = {...originalPoints[vertexIndex]};
	const sphere = visual.spheres[vertexIndex];
	const n = visual.points.length;
	const segmentCount = visual.closed ? n : n - 1;

	const prevEdgeIndex = vertexIndex > 0 ? vertexIndex - 1 : (visual.closed ? segmentCount - 1 : -1);
	const nextEdgeIndex = vertexIndex < segmentCount ? vertexIndex : -1;
	const prevEdge = prevEdgeIndex >= 0 ? visual.edges[prevEdgeIndex] : null;
	const nextEdge = nextEdgeIndex >= 0 ? visual.edges[nextEdgeIndex] : null;
	const prevWasVisible = prevEdge ? prevEdge.visible : false;
	const nextWasVisible = nextEdge && nextEdge !== prevEdge ? nextEdge.visible : false;
	// while previewing, hide the vertex's own real edges - the dashed preview
	// lines below stand in for them
	if(prevEdge) prevEdge.visible = false;
	if(nextEdge && nextEdge !== prevEdge) nextEdge.visible = false;
	sphere.material.emissive.setHex(0x888888); // discreet active-vertex highlight

	const candidateMarker = createMarker(CURSOR_MARKER_COLOR);
	const prevPreviewLine = prevEdge ? createDashedLine(PREVIEW_COLOR) : null;
	const nextPreviewLine = (nextEdge && nextEdge !== prevEdge) ? createDashedLine(PREVIEW_COLOR) : null;
	dm.scene.add(candidateMarker);
	if(prevPreviewLine) dm.scene.add(prevPreviewLine);
	if(nextPreviewLine) dm.scene.add(nextPreviewLine);

	dm._showInstruction(t("Flyt punkt"), t("Flyt musen i punktskyen for at forhåndsvise ny position. Venstreklik bekræfter. ESC annullerer."));

	let currentPicked = null;
	let finished = false;

	const updateScales = () => {
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		if(!candidateMarker.visible) return;
		const s = computeScreenSpaceScale(camera, candidateMarker.getWorldPosition(new THREE.Vector3()), camera.position, size.width, size.height, ScreenSpaceSizes.computedPoint);
		candidateMarker.scale.set(s, s, s);
		if(prevPreviewLine) prevPreviewLine.material.resolution.set(size.width, size.height);
		if(nextPreviewLine) nextPreviewLine.material.resolution.set(size.width, size.height);
	};
	viewer.addEventListener("update", updateScales);

	const hidePreview = () => {
		candidateMarker.visible = false;
		if(prevPreviewLine) prevPreviewLine.visible = false;
		if(nextPreviewLine) nextPreviewLine.visible = false;
		currentPicked = null;
	};

	const onMouseMove = (e) => {
		if(finished || dm.isPaused()) return;
		const picked = dm._pickPlacementPosition(e);
		if(!picked){ hidePreview(); return; }
		currentPicked = picked;
		candidateMarker.position.copy(picked);
		candidateMarker.visible = true;
		if(prevPreviewLine){
			const nb = originalPoints[vertexIndex === 0 ? n - 1 : vertexIndex - 1];
			setLinePositions(prevPreviewLine, new THREE.Vector3(nb.x, nb.y, nb.z), picked);
			prevPreviewLine.visible = true;
		}
		if(nextPreviewLine){
			const nb = originalPoints[(vertexIndex + 1) % n];
			setLinePositions(nextPreviewLine, picked, new THREE.Vector3(nb.x, nb.y, nb.z));
			nextPreviewLine.visible = true;
		}
	};

	const restoreRealEdges = () => {
		if(prevEdge) prevEdge.visible = prevWasVisible;
		if(nextEdge && nextEdge !== prevEdge) nextEdge.visible = nextWasVisible;
	};

	const removePreviewObjects = () => {
		for(const o of [candidateMarker, prevPreviewLine, nextPreviewLine]){
			if(!o) continue;
			dm.scene.remove(o);
			o.geometry.dispose();
			o.material.dispose();
		}
	};

	const cleanup = () => {
		domElement.removeEventListener('mousemove', onMouseMove, false);
		domElement.removeEventListener('mouseup', onMouseUp, false);
		viewer.removeEventListener('update', updateScales);
		viewer.removeEventListener('cancel_insertions', onCancel);
		dm._hideToolHint();
		sphere.material.emissive.setHex(0x000000);
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		removePreviewObjects();
		restoreRealEdges();
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard() || e.button !== THREE.MOUSE.LEFT) return;
		if(!currentPicked) return;

		finished = true;
		cleanup();
		removePreviewObjects();
		restoreRealEdges();

		const result = movePoint(originalPoints, vertexIndex, {x: currentPicked.x, y: currentPicked.y, z: currentPicked.z});
		dm.applyFeatureGeometry(visual, toVector3Array(result.points));
		dm.checkVertexMoveConnectionWarning(visual, vertexIndex, previousPosition);
	};

	domElement.addEventListener('mousemove', onMouseMove, false);
	domElement.addEventListener('mouseup', onMouseUp, false);
	viewer.addEventListener('cancel_insertions', onCancel);
}

// ── Flyt punkt i Z ─────────────────────────────────────────────────────────
//
// Unlike Flyt punkt this is NOT an interactive point-cloud pick - the user
// gives an exact, signed distance in cm via promptSignedDistanceCm() (task:
// "popup dialog hvor brugeren enten kan skrive eller vælge antal cm punktet
// skal flyttes, både positiv og negativ"), so it commits in one step once
// that resolves. Applies to ANY vertex (standalone Point / interior line
// vertex / endpoint - see EditingCapabilities.js's canMoveVertexZ). Same
// commit shape as every other vertex-position operation here: pure
// geometry (movePointInZ()) -> applyFeatureGeometry() ->
// checkVertexMoveConnectionWarning() (moving a vertex in Z can break a
// coincidence with another feature's endpoint exactly like moving it in
// X/Y can, so the same post-move toast applies).
async function runMovePointZ(dm, visual, vertexIndex){
	const originalPoints = toPlainPoints(visual);
	const previousPosition = {...originalPoints[vertexIndex]};

	const deltaZ = await promptSignedDistanceCm(dm, {
		title: t("Flyt punkt i Z"),
		description: t("Flytter det valgte punkt lodret. Positivt tal flytter op, negativt tal flytter ned."),
	});
	if(deltaZ === undefined) return;

	const result = movePointInZ(originalPoints, vertexIndex, deltaZ);
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
	dm.checkVertexMoveConnectionWarning(visual, vertexIndex, previousPosition);
}

// ── Flyt linje / segment / del i Z ────────────────────────────────────────
//
// The multi-vertex sibling of "Flyt punkt i Z": the same commit shape, but every vertex of the span moves by the same Δz
// (geometry/vectorEditing.js's movePointsInZ()), chosen in a dialog with a distance or pipe dimension (promptMoveLineZ()).
// `scope` decides the span and the wording:
//   "line"    - the whole line (whole-object menu): vertices 0..last
//   "segment" - the clicked segment's two vertices (segment menu)
//   "range"   - the selected range's vertices (range menu)
// A moved ENDPOINT can break a junction with another feature exactly like a single-vertex Z move can, so the same post-move toast check runs
// for each moved endpoint.
async function runMoveLineZ(dm, visual, scope, start, end){
	const originalPoints = toPlainPoints(visual);
	const [from, to] = [start === undefined ? 0 : start, end === undefined ? originalPoints.length - 1 : end];
	const wording = scope === "segment"
		? {title: t("Flyt segment i Z"), description: t("Flytter segmentets to punkter lodret med en valgt afstand eller rørdimension.")}
		: scope === "range"
			? {title: t("Flyt del i Z"), description: t("Flytter den valgte dels punkter lodret med en valgt afstand eller rørdimension.")}
			: {title: t("Flyt linje i Z"), description: t("Flytter alle linjens punkter lodret med en valgt afstand eller rørdimension.")};

	const deltaZ = await promptMoveLineZ(dm, wording);
	if(deltaZ === undefined) return;

	const movedEndpoints = [0, originalPoints.length - 1].filter((index) => index >= from && index <= to);
	const previousPositions = movedEndpoints.map((index) => ({index, position: {...originalPoints[index]}}));

	const result = movePointsInZ(originalPoints, from, to, deltaZ);
	if(!dm.applyFeatureGeometry(visual, toVector3Array(result.points))) return;
	for(const {index, position} of previousPositions) dm.checkVertexMoveConnectionWarning(visual, index, position);
}

// ── Sæt kote (absolut Z) ─────────────────────────────────────────────────
//
// Sibling of "Flyt punkt i Z" above - same universal any-vertex/any-role
// applicability (task §11) and the same commit shape - but the dialog asks
// for the target kote directly (setAbsoluteZ(), z' = z_input) rather than a
// relative offset.
async function runSetAbsoluteZ(dm, visual, vertexIndex){
	const originalPoints = toPlainPoints(visual);
	const previousPosition = {...originalPoints[vertexIndex]};

	const zValue = await promptAbsoluteZ(dm, {
		title: t("Sæt kote"),
		description: t("Sætter det valgte punkts absolutte kote (Z). X/Y ændres ikke."),
		initialValue: previousPosition.z.toFixed(3),
	});
	if(zValue === undefined) return;

	const result = setAbsoluteZ(originalPoints, vertexIndex, zValue);
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
	dm.checkVertexMoveConnectionWarning(visual, vertexIndex, previousPosition);
}

// ── Snap til nærmeste punktsky punkt (vertex + whole-feature menus) ──────
//
// One-click, no interactive preview (unlike Flyt punkt): snaps the selected
// vertex straight to the point-cloud point actually nearest its OWN current
// position, by real 3D distance - a true spatial search (see
// DigitizationModule._pickPointCloudPositionAt() /
// Utils.findNearestPointCloudPoint()), NOT a screen-ray/viewing-direction
// pick - user-reported: the distance to the point-cloud point must be what
// decides the result, not whatever a camera ray happens to hit first.
// Applies to Point/LineString/Polygon alike - it moves the SELECTED vertex
// only, through the same movePoint()/applyFeatureGeometry() commit path Flyt
// punkt uses. A coincidence at that vertex is not propagated to other
// features (stabilisation task §1.1 / §3); if the move broke one,
// checkVertexMoveConnectionWarning() surfaces it as a toast.
function runSnapToPointCloud(dm, visual, vertexIndex){
	const point = visual.points[vertexIndex];
	if(!point) return;

	const previousPosition = {x: point.position.x, y: point.position.y, z: point.position.z};
	const snapped = dm._pickPointCloudPositionAt(point.position);
	if(!snapped){
		showGeometryWarning(dm, t("Snap til nærmeste punktsky punkt"), t("Der blev ikke fundet noget punktskypunkt ved dette punkt."));
		return;
	}

	const result = movePoint(toPlainPoints(visual), vertexIndex, {x: snapped.x, y: snapped.y, z: snapped.z});
	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
	dm.checkVertexMoveConnectionWarning(visual, vertexIndex, previousPosition);
}

// Whole-feature counterpart (task: "det skal også virke for linjer og
// polygoner... det er blot alle punkter der skal snappes") - independently
// spatial-snaps EVERY vertex to its own nearest point-cloud point (each
// vertex gets its own search, not one shared result), then commits the full
// resulting array in a single applyFeatureGeometry() call rather than one
// commit per vertex. A vertex with no point-cloud point found nearby (see
// Utils.findNearestPointCloudPoint()'s maxRadius) is left at its original
// position rather than aborting the whole operation - the rest of the
// feature still benefits from whichever vertices DID find something to
// snap to.
function runSnapAllToPointCloud(dm, visual){
	const points = toPlainPoints(visual);
	const result = points.map((p) => {
		const snapped = dm._pickPointCloudPositionAt(p);
		return snapped ? {x: snapped.x, y: snapped.y, z: snapped.z} : p;
	});
	dm.applyFeatureGeometry(visual, toVector3Array(result));
}

// ── Snap til andet punkt (vertex menu) ───────────────────────────────────
//
// A precise, controlled way to make one existing point coincide with
// another. The SOURCE vertex is already chosen (it is why this menu opened);
// PointView then enters a short pick state where the user selects the TARGET
// vertex/Point-feature. The source is moved to the target's EXACT coordinate
// (full 3D, incl. Z). The TARGET feature is never modified.
//
// One of the two operations where the user has EXPLICITLY asked for a precise
// XYZ coincidence (the other is "Forbind til andet endepunkt"), so the
// target's Z is adopted verbatim. Acts on the SELECTED source vertex only - a
// coincidence at the source is not propagated to other features (stabilisation
// task §1.1 / §9). Commit is movePoint() + applyFeatureGeometry(); target
// coordinates come from the picked vertex's real geometry, never a point-cloud
// pick; a click that does not land on a valid vertex leaves the pick state
// running (ESC cancels with no change).
function runSnapToOtherPoint(dm, visual, vertexIndex){
	const sourceFeature = dm.getFeature(visual);
	if(!sourceFeature) return;

	const domElement = dm.viewer.renderer.domElement;
	const sourcePos = visual.points[vertexIndex].position;

	let finished = false;
	const previewLine = createDashedLine(PREVIEW_COLOR);
	dm.scene.add(previewLine);

	if(visual.setVertexHighlight) visual.setVertexHighlight(vertexIndex, true);
	dm._showInstruction(t("Snap til andet punkt"), t("Vælg punktet der skal snappes til. ESC annullerer."));

	// a valid target is any real feature's vertex/Point-feature except the
	// exact source vertex itself (see geometry/snapToPoint.js)
	const isValidTarget = (tVisual, tIndex) => {
		const tFeature = tVisual && dm.getFeature(tVisual);
		if(!tFeature) return false;
		return isValidSnapTarget({featureId: sourceFeature.id, vertexIndex}, {featureId: tFeature.id, vertexIndex: tIndex});
	};

	const updateResolution = () => {
		const size = dm.viewer.renderer.getSize(new THREE.Vector2());
		previewLine.material.resolution.set(size.width, size.height);
	};

	const cleanup = () => {
		domElement.removeEventListener('mousemove', onMove, false);
		domElement.removeEventListener('mouseup', onPick, false);
		dm.viewer.removeEventListener('update', updateResolution);
		dm.viewer.removeEventListener('cancel_insertions', onCancel);
		dm._hideToolHint();
		if(visual.setVertexHighlight) visual.setVertexHighlight(vertexIndex, false);
		dm.scene.remove(previewLine);
		previewLine.geometry.dispose();
		previewLine.material.dispose();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		// nothing was ever applied - the source geometry is untouched
	};

	// live preview: a dashed connector from the source vertex to whatever
	// valid target is currently under the cursor (the target sphere's own
	// native hover emissive already marks it too - see FeatureVisual._wireSphere())
	const onMove = () => {
		if(finished) return;
		const hit = dm._findHoveredVertex();
		if(hit && isValidTarget(hit.visual, hit.index)){
			setLinePositions(previewLine, sourcePos, hit.visual.points[hit.index].position);
			previewLine.visible = true;
		}else{
			previewLine.visible = false;
		}
	};

	const onPick = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard() || e.button !== THREE.MOUSE.LEFT) return;

		const hit = dm._findHoveredVertex();
		if(!hit || !isValidTarget(hit.visual, hit.index)) return; // not a valid target - stay in pick mode, never move the source

		finished = true;
		cleanup();

		const targetPos = hit.visual.points[hit.index].position;
		const previousPosition = {x: sourcePos.x, y: sourcePos.y, z: sourcePos.z};
		const move = snapMoveToTarget(
			{featureId: sourceFeature.id, vertexIndex},
			{x: targetPos.x, y: targetPos.y, z: targetPos.z},
		);
		const result = movePoint(toPlainPoints(visual), move.vertexIndex, move.position);
		dm.applyFeatureGeometry(visual, toVector3Array(result.points));
		dm.checkVertexMoveConnectionWarning(visual, vertexIndex, previousPosition);
	};

	updateResolution();
	dm.viewer.addEventListener('update', updateResolution);
	domElement.addEventListener('mousemove', onMove, false);
	domElement.addEventListener('mouseup', onPick, false);
	dm.viewer.addEventListener('cancel_insertions', onCancel);
}

// Thin wrapper over EditingCapabilities.js's pure resolveFixedAngleVertexIndex()
// - reads the one piece of real-visual state (vertex count) it needs. The
// same pure function also backs the "is Fast vinkel even applicable here"
// capability check, so the two never disagree.
function resolveFixedAngleVertex(visual, hit){
	return resolveFixedAngleVertexIndex(visual.points.length, hit.segmentIndex, hit.t);
}

async function runFixedAngle(dm, visual, vertexIndex){
	if(vertexIndex === null){
		showGeometryWarning(dm, t("Fast vinkel"), t("Fast vinkel kræver et vertex med et segment på hver side (ikke vektorens første eller sidste punkt)."));
		return;
	}

	const angle = await promptFixedAngle(dm, 90);
	if(angle === undefined) return; // cancelled

	const points = toPlainPoints(visual);
	const A = points[vertexIndex - 1], C = points[vertexIndex], B = points[vertexIndex + 1];
	const maxSagitta = getMaxSagitta();
	const result = computeFixedAngleVertex(A, C, B, angle, maxSagitta);

	if(!result){
		// re-derive the actual (ungated) deviation via the same closed-form
		// solve, purely for an honest message - computeFixedAngleVertex()
		// itself only reports pass/fail, not by how much it missed
		const solved = projectPointToFixedAngleArc(A, C, B, angle);
		refuseOrWarn(dm, t("Fast vinkel"), dist2D(solved, C), maxSagitta);
		return;
	}

	const out = points.map((p) => ({x: p.x, y: p.y, z: p.z}));
	out[vertexIndex] = result.point;
	dm.applyFeatureGeometry(visual, toVector3Array(out));
}

// Interactive reference-segment picking (section 9): shows an instruction,
// waits for the next left-click on any OTHER segment (own vector included,
// as long as it isn't the same segment), computes the aligned result, and
// applies immediately (the operation has no further free parameter once
// the reference is chosen, so there is nothing additional to preview
// beyond what's already visible via the segment's own on-screen position
// changing - see section 12: preview must be the same computed result that
// gets committed, which this trivially satisfies by computing exactly once).
// ESC cancels and restores the instruction overlay.
function runAlignToReference(dm, visual, segmentIndex, mode){
	const domElement = dm.viewer.renderer.domElement;
	const title = mode === "parallel" ? t("Parallel med reference") : t("Vinkelret på reference");
	dm._showInstruction(title, t("Klik på et referencesegment på en digitaliseret vektor. ESC annullerer."));

	let finished = false;

	const cleanup = () => {
		domElement.removeEventListener('mouseup', onPick, false);
		dm.viewer.removeEventListener('cancel_insertions', onCancel);
		dm._hideToolHint();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
	};

	const onPick = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard() || e.button !== THREE.MOUSE.LEFT) return;

		const refHit = dm._findHoveredSegment(e);
		if(!refHit) return;
		if(refHit.visual === visual && refHit.segmentIndex === segmentIndex) return; // can't reference itself

		finished = true;
		cleanup();

		const a = refHit.visual.points[refHit.segmentIndex].position;
		const b = refHit.visual.points[refHit.segmentIndex + 1].position;
		const refDir = {x: b.x - a.x, y: b.y - a.y};

		const points = toPlainPoints(visual);
		const maxSagitta = getMaxSagitta();
		const aligned = computeSegmentAlignment(points, segmentIndex, refDir, mode);
		const fn = mode === "parallel" ? alignSegmentParallel : alignSegmentPerpendicular;
		const result = fn(points, segmentIndex, refDir, maxSagitta);

		if(!result){
			refuseOrWarn(dm, title, aligned ? aligned.maxDeviation : Infinity, maxSagitta);
			return;
		}

		dm.applyFeatureGeometry(visual, toVector3Array(result.points));
	};

	domElement.addEventListener('mouseup', onPick, false);
	dm.viewer.addEventListener('cancel_insertions', onCancel);
}

// ── Linje-topologi: Del/Saml/Forlæng/Trim/Forbind endpoints/Sæt punkt ved
//    skæring ────────────────────────────────────────────────────────────
//
// All cross-feature reference-picking below is a generalisation of
// runAlignToReference()'s own click-to-pick pattern above (one left-click,
// ESC-cancelable via cancel_insertions, no live preview beyond the result
// itself - see that function's own doc comment for why): a title/instruction
// is shown, the next left-click is hit-tested, and a caller-supplied
// predicate decides whether that hit is a usable reference (an invalid pick
// - e.g. clicking the source feature itself - is silently ignored, letting
// the user try again, exactly like runAlignToReference()'s own "can't
// reference itself" check). Kept as separate, parallel helpers rather than
// refactoring runAlignToReference() itself onto them - that function is
// already tested/shipped and unrelated to this task's own scope (task
// section 10: avoid unrelated refactors).
function pickReferenceLine(dm, title, isValidReference, onPicked){
	const domElement = dm.viewer.renderer.domElement;
	dm._showInstruction(title, t("Klik på en referencelinje. ESC annullerer."));

	let finished = false;
	const cleanup = () => {
		domElement.removeEventListener('mouseup', onPick, false);
		dm.viewer.removeEventListener('cancel_insertions', onCancel);
		dm._hideToolHint();
	};
	const onCancel = () => { if(finished) return; finished = true; cleanup(); };

	const onPick = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard() || e.button !== THREE.MOUSE.LEFT) return;
		const refHit = dm._findHoveredSegment(e);
		if(!refHit || !isValidReference(refHit.visual)) return;
		finished = true;
		cleanup();
		onPicked(refHit.visual);
	};

	domElement.addEventListener('mouseup', onPick, false);
	dm.viewer.addEventListener('cancel_insertions', onCancel);
}

// Vertex-hit counterpart of pickReferenceLine() above, for Forbind til
// andet endpoint - a click is only useful there if it lands on a VERTEX
// (an endpoint specifically), never a segment, so this uses
// dm._findHoveredVertex() (native hover-state, same mechanism the ordinary
// Shift+Right-click vertex menu itself relies on) instead of
// _findHoveredSegment().
function pickReferenceEndpoint(dm, title, isValidReference, onPicked){
	const domElement = dm.viewer.renderer.domElement;
	dm._showInstruction(title, t("Klik på et andet endepunkt. ESC annullerer."));

	let finished = false;
	const cleanup = () => {
		domElement.removeEventListener('mouseup', onPick, false);
		dm.viewer.removeEventListener('cancel_insertions', onCancel);
		dm._hideToolHint();
	};
	const onCancel = () => { if(finished) return; finished = true; cleanup(); };

	const onPick = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard() || e.button !== THREE.MOUSE.LEFT) return;
		const hit = dm._findHoveredVertex();
		if(!hit || !isValidReference(hit.visual, hit.index)) return;
		finished = true;
		cleanup();
		onPicked(hit.visual, hit.index);
	};

	domElement.addEventListener('mouseup', onPick, false);
	dm.viewer.addEventListener('cancel_insertions', onCancel);
}

// Del/Saml/Forlæng/Trim/Sæt punkt ved skæring are all restricted to plain,
// ordinary LineString features (kind==="linestring") - not crossprofile
// (its own specialised structure/lifecycle) and not Polygon (no endpoints) -
// see EditingCapabilities.js's own header comment for why. This is the one
// place that restriction is actually enforced against a REFERENCE pick
// (the acted-upon side is already gated by EditingCapabilities itself, via
// which menu item even appears).
function isPlainLineStringReference(dm, refVisual, excludeVisual){
	if(refVisual === excludeVisual) return false;
	if(refVisual.geometryType !== "LineString") return false;
	return resolveObjectKind(dm, refVisual) === "linestring";
}

function isEndpointVertex(refVisual, refIndex){
	if(!refVisual.points) return false;
	if(refVisual.geometryType === "Point") return refIndex === 0;
	return refVisual.geometryType === "LineString" && (refIndex === 0 || refIndex === refVisual.points.length - 1);
}


// ── Del linje ─────────────────────────────────────────────────────────────
//
// The one operation in this group that produces a SECOND feature (see
// geometry/lineSplit.js's splitPolylineAt() - "first" replaces the original
// feature's own geometry via the ordinary applyFeatureGeometry() commit
// path; "second" becomes a brand new Feature+FeatureVisual via
// DigitizationModule.registerNewFeature(), which handles the visuals map/
// FeatureManager/snap-targets/geometry_changed wiring in one place - see
// its own doc comment). The new feature copies the original's category/
// tool/featureType (still "the same kind of thing", task requirement) but
// gets its own name/id - "hvordan attributter og feature identity bør
// håndteres": deliberately NOT a deep-copy of `attributes` (e.g. a
// crossSection report payload would make no sense duplicated onto a plain
// LineString half, and this operation is restricted to plain LineString
// anyway - see isPlainLineStringReference()'s own comment).
function runSplitLine(dm, visual, segmentIndex, param){ // ikke "t": det ville skygge for i18n-funktionen t()
	const points = toPlainPoints(visual);
	const result = splitPolylineAt(points, segmentIndex, param);
	if(!result){
		showGeometryWarning(dm, t("Del linje"), t("Deling her ville give en del med færre end 2 punkter."));
		return;
	}

	const feature = dm.getFeature(visual);
	if(!feature) return;

	dm.applyFeatureGeometry(visual, toVector3Array(result.first));

	const secondFeature = createFeature({
		geometryType: "LineString",
		coordinates: result.second.map((p) => [p.x, p.y, p.z]),
		category: feature.properties.category,
		tool: feature.properties.tool,
		featureType: feature.properties.featureType,
		name: `${feature.properties.name} (del 2)`,
		// Del linje er en redigeringshandling på en EKSISTERENDE feature: den nye halvdel hører til den ORIGINALE models, ikke til den
		// model, der tilfældigvis er aktiv nu (ellers ville _wireFeatureLifecycle() stemple den aktive model).
		pointcloudId: feature.pointcloudId,
	});

	const secondVisual = new FeatureVisual("LineString");
	dm.scene.add(secondVisual);
	secondVisual.setPoints(toVector3Array(result.second));
	secondVisual.setInfo(secondFeature.properties.name, secondFeature.properties.description);
	dm.registerNewFeature(secondFeature, secondVisual);
}

// ── Saml med anden linje ──────────────────────────────────────────────────
//
// The clicked-from feature is the survivor (keeps its id/properties); the
// picked reference feature is removed once merged in - documented,
// deliberately asymmetric choice (task: "hvordan attributter og feature
// identity bør håndteres"), consistent with every other reference-picking
// operation in this file (the acted-upon side changes, the reference does
// not - see runAlignToReference()'s own doc comment).
function runMergeLines(dm, visual){
	pickReferenceLine(dm, t("Saml med anden linje"), (refVisual) => isPlainLineStringReference(dm, refVisual, visual), (refVisual) => {
		const otherFeature = dm.getFeature(refVisual);
		if(!otherFeature) return;

		const result = mergePolylines(toPlainPoints(visual), toPlainPoints(refVisual), {toleranceGap: getPlanarTolerance()});
		if(result.error){
			showGeometryWarning(dm, t("Saml med anden linje"), t("De valgte linjer mødes ikke inden for tolerancen (nærmeste afstand: {0}).", formatLength(result.closestGap)));
			return;
		}

		dm.applyFeatureGeometry(visual, toVector3Array(result.points));
		dm.removeFeature(otherFeature.id);
	});
}

// ── Fortsæt linje… ────────────────────────────────────────────────────────
// Resumes ordinary line digitization from an existing LineString's endpoint:
// left-click adds a point, right-click finishes, ESC discards only what THIS
// session added - the same interaction DigitizationModule.startInsertion()
// already gives a brand-new Line (task: "Normal afslutning, preview og
// ESC/cancel skal fungere som ved almindelig linjedigitalisering"), just
// applied to the SAME FeatureVisual/feature instead of creating a new one
// (task: "Det eksisterende objekt skal bevares som samme feature").
//
// New vertices are appended after the last point (vertexIndex is the LAST
// index) or PREPENDED before the first (vertexIndex === 0), via
// FeatureVisual's own appendAtEnd()/addMarker(position, 0) - both already
// support an arbitrary insert index (see FeatureVisual.js's addMarker() doc
// comment), and both already call _sync(), which fires "geometry_changed".
// That listener was wired ONCE for this feature back when it was first
// created (DigitizationModule._wireFeatureLifecycle()) and stays live for
// the feature's whole life, so feature.geometry.coordinates and snap
// targets are kept in sync automatically here too - no second sync path
// needed, exactly like every other in-place edit in this file.
function runContinueLine(dm, visual, vertexIndex){
	const feature = dm.getFeature(visual);
	if(!feature) return;

	const fromStart = vertexIndex === 0;
	const domElement = dm.viewer.renderer.domElement;
	const originalPositions = visual.points.map((p) => p.position.clone());

	let finished = false;

	dm._showInstruction(t("Fortsæt linje"), t("Venstreklik for at tilføje punkter. Højreklik for at afslutte."));

	const cleanup = () => {
		domElement.removeEventListener('mouseup', onMouseUp, false);
		dm.viewer.removeEventListener('cancel_insertions', onCancel);
		dm._hideToolHint();
	};

	const onCancel = (e) => {
		if(finished) return;
		finished = true;
		cleanup();
		if(e && e.discard){
			// ESC: discard only what THIS session added - restores the feature to
			// exactly its previously-committed geometry, same "Annuller gendanner
			// præcis den tidligere geometri" convention as every other cancel
			// path in this file (e.g. circleTool.js/rectangleTool.js's dialogs).
			dm.applyFeatureGeometry(visual, originalPositions);
		}
		// tool switched away / stopped externally (not ESC): keep whatever was
		// already added, same convention as the generic insertion flow's own
		// non-discard cancel path (DigitizationModule.startInsertion()'s onCancel)
	};

	const finish = () => {
		if(finished) return;
		finished = true;
		cleanup();
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;

		if(e.button === THREE.MOUSE.LEFT){
			const location = dm._pickPlacementPosition(e);
			if(!location) return;
			if(fromStart) visual.addMarker(location, 0);
			else visual.appendAtEnd(location);
		}else if(e.button === THREE.MOUSE.RIGHT){
			finish();
		}
	};

	domElement.addEventListener('mouseup', onMouseUp, false);
	dm.viewer.addEventListener('cancel_insertions', onCancel);
}

// ── Forlæng til reference (XY / plan, §7) ────────────────────────────────
function runExtendToReference(dm, visual, vertexIndex){
	pickReferenceLine(dm, t("Forlæng til reference"), (refVisual) => isPlainLineStringReference(dm, refVisual, visual), (refVisual) => {
		// Plan operation: the ray meets the reference in XY; the new endpoint's
		// Z is extrapolated from the source's own terminal segment, never taken
		// from the reference (see extendPolylineToReference()).
		const result = extendPolylineToReference(toPlainPoints(visual), vertexIndex, toPlainPoints(refVisual), {
			planarTolerance: getPlanarTolerance(),
		});

		if(result.error){
			showGeometryWarning(dm, t("Forlæng til reference"), t("Linjen når ikke referencegeometrien i den retning, den peger."));
			return;
		}

		dm.applyFeatureGeometry(visual, toVector3Array(result.points));
	});
}

// ── Forlæng endesegment (fast afstand i egen retning) ────────────────────
//
// Sibling of Forlæng til reference, same GEOMETRI grouping and endpoint-only
// gate (canExtendSegment), but single-feature: no reference is picked at
// all - the user gives an exact, signed cm distance (promptSignedDistanceCm(),
// same dialog "Flyt punkt i Z" uses), and the endpoint moves along its own
// terminal segment's direction by exactly that (see
// geometry/vectorEditing.js's extendEndpointByDistance() for the sign
// convention: positive lengthens, negative shortens).
async function runExtendEndSegment(dm, visual, vertexIndex){
	const distanceM = await promptSignedDistanceCm(dm, {
		title: t("Forlæng endesegment"),
		description: t("Flytter endepunktet langs endesegmentets egen retning. Positivt tal forlænger linjen, negativt tal afkorter den."),
	});
	if(distanceM === undefined) return;

	const result = extendEndpointByDistance(toPlainPoints(visual), vertexIndex, distanceM);
	if(result.error === "would-collapse-segment"){
		showGeometryWarning(dm, t("Forlæng endesegment"), t("Afkortningen ville flytte endepunktet til eller forbi dets eget naboendepunkt."));
		return;
	}
	if(result.error === "degenerate-segment"){
		showGeometryWarning(dm, t("Forlæng endesegment"), t("Endesegmentet har ingen udstrækning at forlænge i retningen af."));
		return;
	}
	if(result.error) return; // too-few-points/not-an-endpoint: menu item shouldn't have shown at all

	dm.applyFeatureGeometry(visual, toVector3Array(result.points));
}

// ── Trim til reference (XY / plan, §6 - same model as Trim mod linje) ────
function runTrimToReference(dm, visual, vertexIndex){
	pickReferenceLine(dm, t("Trim til reference"), (refVisual) => isPlainLineStringReference(dm, refVisual, visual), (refVisual) => {
		const result = trimPolylineToReference(toPlainPoints(visual), vertexIndex, toPlainPoints(refVisual), {
			planarTolerance: getPlanarTolerance(),
		});

		if(result.error === "result-too-short"){
			showGeometryWarning(dm, t("Trim til reference"), t("Operationen blev ikke gennemført, fordi resultatet ville have færre end 2 punkter."));
			return;
		}
		if(result.error){
			showGeometryWarning(dm, t("Trim til reference"), t("Linjen krydser ikke referencegeometrien."));
			return;
		}

		dm.applyFeatureGeometry(visual, toVector3Array(result.points));
	});
}

// ── Trim mod linje ───────────────────────────────────────────────────────
//
// Distinct from runTrimToReference() (vertex menu, endpoint-first, reference
// left untouched) only by WORKFLOW - the acted-upon side is chosen from the
// CURRENT selection (a segment, or the whole line), and the reference gets a
// vertex inserted at the crossing so a later operation can snap to it. Same
// XY intersection model and same Z rule as every other intersection tool
// (§1.4): the trim line's cut end takes the SOURCE line's own Z at the
// crossing; the reference's new vertex takes the REFERENCE line's Z there -
// they share X/Y, not Z. Both features commit through applyFeatureGeometry();
// the reference is only committed when a vertex was actually inserted.
function runTrimAndBridge(dm, visual, keepSegmentIndex){
	pickReferenceLine(dm, t("Trim mod linje"), (refVisual) => isPlainLineStringReference(dm, refVisual, visual), (refVisual) => {
		const result = trimPolylineAndBridge(toPlainPoints(visual), toPlainPoints(refVisual), {
			keepSegmentIndex,
			planarTolerance: getPlanarTolerance(),
		});

		if(result.error === "result-too-short"){
			showGeometryWarning(dm, t("Trim mod linje"), t("Operationen blev ikke gennemført, fordi resultatet ville have færre end 2 punkter."));
			return;
		}
		if(result.error){
			showGeometryWarning(dm, t("Trim mod linje"), t("Linjerne krydser ikke hinanden set fra oven (i planen). Kontrollér at linjerne faktisk skærer hinanden."));
			return;
		}

		if(result.insertedIntoReference){
			dm.applyFeatureGeometry(refVisual, toVector3Array(result.referencePoints));
		}
		dm.applyFeatureGeometry(visual, toVector3Array(result.trimmedPoints));
	});
}

// ── Forbind til andet endpoint ────────────────────────────────────────────
//
// Moves THIS endpoint exactly onto the picked one (geometry/lineSplit.js's
// connectEndpoints() - a thin wrapper over vectorEditing.js's own
// movePoint()) - the picked feature is never itself modified. Same
// underlying operation the Topology-QA panel's "Luk gap" quick-action calls
// for a detected near-miss (see TopologyQaPanel.js) - one implementation,
// two entry points.
function runConnectToEndpoint(dm, visual, vertexIndex){
	pickReferenceEndpoint(
		dm, t("Forbind til andet endpoint"),
		(refVisual, refIndex) => !(refVisual === visual && refIndex === vertexIndex) && isEndpointVertex(refVisual, refIndex),
		(refVisual, refIndex) => {
			// captured BEFORE the move - the warning is about a connection
			// this vertex's OLD position may have had to a THIRD feature
			// (silently broken by moving it here), never about the new
			// connection to refVisual itself (that one is the intended,
			// successful result of this exact operation, not something to
			// warn about)
			const previousPosition = {x: visual.points[vertexIndex].position.x, y: visual.points[vertexIndex].position.y, z: visual.points[vertexIndex].position.z};
			const result = connectEndpoints(toPlainPoints(visual), vertexIndex, toPlainPoints(refVisual), refIndex);
			dm.applyFeatureGeometry(visual, toVector3Array(result.points));
			dm.checkVertexMoveConnectionWarning(visual, vertexIndex, previousPosition);
		},
	);
}

// ── Sæt punkt ved skæring (XY / plan, §8) ───────────────────────────────
function runIntersectInsert(dm, visual, hit){
	pickReferenceLine(dm, t("Sæt punkt ved skæring"), (refVisual) => isPlainLineStringReference(dm, refVisual, visual), (refVisual) => {
		// Plan operation: the new vertex's X/Y is the XY crossing, its Z is
		// interpolated along the SOURCE segment - the reference is not modified.
		const result = intersectAndInsert(toPlainPoints(visual), toPlainPoints(refVisual), hit.point, {
			planarTolerance: getPlanarTolerance(),
		});

		if(result.error){
			showGeometryWarning(dm, t("Sæt punkt ved skæring"), t("Linjerne krydser ikke hinanden set fra oven (i planen)."));
			return;
		}

		dm.applyFeatureGeometry(visual, toVector3Array(result.points));
	});
}

// ── Omtegn del (Reshape, range menu only - see showRangeOperationsMenu()) ──
//
// Reuses DigitizationModule._captureVector() UNCHANGED - the same throwaway,
// never-registered construction-polyline capture Midline/Vinkelret/
// Parallelforskydning already use for their own baseline digitizing (left-
// click adds a point, right-click/ESC finishes) - then splices the freshly
// drawn chain in between the range's own UNCHANGED start/end anchor
// vertices and commits through the one existing applyFeatureGeometry() path.
// ESC before finishing leaves the original feature numerically untouched -
// nothing is applied until the fresh chain is actually finished and spliced.
function runReshapeRange(dm, visual, range){
	dm._showInstruction(t("Omtegn del"), t("Tegn det nye forløb for den valgte del. Venstreklik tilføjer punkt, højreklik afslutter. ESC annullerer."));

	dm._captureVector((freshVisual) => {
		// unlike a tool's own capture flow (which stays active and always
		// shows a fresh instruction for its next cycle - see startInsertion()),
		// nothing else takes over once this one-shot capture ends, so the
		// hint must be hidden explicitly here - both on success and on
		// cancel/too-few-points, matching every other interactive
		// VectorEditingMenu.js flow's own cleanup() (runMovePoint,
		// runAlignToReference, startSegmentSelection all do the same)
		dm._hideToolHint();

		if(!freshVisual) return; // cancelled, or fewer than 2 points captured

		const newChain = toPlainPoints(freshVisual);
		dm.scene.remove(freshVisual);

		const points = toPlainPoints(visual);
		const spliced = [...points.slice(0, range.start + 1), ...newChain, ...points.slice(range.end)];
		dm.applyFeatureGeometry(visual, toVector3Array(spliced));
	});
}

// ── Vælg sammenhængende del (section 6) ──────────────────────────────────
//
// A contiguous vertex-index range on the SAME feature, picked with two
// plain left-clicks (start, then end) on its existing segments - never
// creates new geometry, purely an index range. Highlighted by recoloring
// the involved edges to the same orange FeatureVisual already uses for a
// selected feature (COLOR_SELECTED) - reusing that convention rather than
// inventing a second highlight colour/style.
function highlightRange(visual, start, end){
	for(let i = start; i < end; i++){
		if(visual.edges[i]) visual.edges[i].material.color.setHex(COLOR_HIGHLIGHT);
	}
}

function clearHighlight(visual){
	const color = visual.selected ? 0xffa500 : 0x2f8fd6; // FeatureVisual's own COLOR_SELECTED/COLOR_DEFAULT
	for(const edge of visual.edges) edge.material.color.setHex(color);
}

// Shows a grouped menu that carries a transient highlight (already applied
// by the caller before this runs - highlightRange() for a single segment or
// a picked range) and clears it once the menu goes away, one way or
// another: an item was selected (its own onSelect() has already run by
// then, so its own preview/instruction state takes over - task section 11:
// "når operation starter: selection/highlight overgår til operationens
// preview-state"), the user clicked outside, or pressed ESC. One shared
// implementation for both the segment-menu's single-edge highlight and Vælg
// sammenhængende del's own range highlight, rather than two copies of the
// same deferred-listener bookkeeping.
function showMenuWithHighlightClear(clientX, clientY, menuOptions, onClear){
	showGroupedContextMenu(clientX, clientY, menuOptions);

	const clearOnce = () => {
		onClear();
		document.removeEventListener('mousedown', clearOnce, true);
		document.removeEventListener('keydown', clearOnceOnEscape);
	};
	const clearOnceOnEscape = (e) => { if(e.key === 'Escape') clearOnce(); };
	setTimeout(() => {
		document.addEventListener('mousedown', clearOnce, true);
		document.addEventListener('keydown', clearOnceOnEscape);
	}, 0);
}

// ── Bue fra pilhøjde / Rediger bue / Vend bue / Opløs bue (task §1-§10) ──
//
// The chord is always the range's own two boundary vertices - only interior
// vertices ever move (see geometry/arcFromSagitta.js). Reconstructs the
// arc's plane {d,u} from `arcRange.planeU` (an existing arc being edited) or
// derives a fresh one from the chord + current interior geometry
// (determineArcPlane() - a brand-new arc); see arcRangeStore.js's own header
// comment for why only `planeU` (not `d`) needs to be persisted.
function planeFromArcRange(points, arcRange){
	const chordStart = points[arcRange.startIndex];
	const chordEnd = points[arcRange.endIndex];
	const chordVec = {x: chordEnd.x - chordStart.x, y: chordEnd.y - chordStart.y, z: chordEnd.z - chordStart.z};
	const chordLen = Math.hypot(chordVec.x, chordVec.y, chordVec.z);
	const d = chordLen > 1e-9
		? {x: chordVec.x / chordLen, y: chordVec.y / chordLen, z: chordVec.z / chordLen}
		: {x: 1, y: 0, z: 0};
	return {d, u: {x: arcRange.planeU[0], y: arcRange.planeU[1], z: arcRange.planeU[2]}};
}

// Live-preview dialog shared by "Bue fra pilhøjde…" (brand-new arc,
// `existingRange` omitted) and "Rediger bue…" (opened with the stored
// sagitta/plane, `existingRange` given) - task §2/§7. Reuses
// visual.setPoints() directly for the live preview (task's own "genbrug
// eksisterende... preview... geometry_changed" instruction, and this
// session's own earlier design decision to avoid a parallel ghost-geometry
// renderer) - every keystroke goes through the exact same commit path
// (geometry_changed -> FeatureManager.updateGeometry -> snap-target refresh
// -> arcRangeStore reconciliation, see DigitizationModule._wireFeatureLifecycle())
// a permanent commit would, so Registreringer/labels/snap targets stay live
// and correct while the dialog is still open.
//
// Vertex count is DYNAMIC (task §2/§3, a later revision of this feature):
// the arc's own [startIndex..endIndex] span is resampled fresh on every
// keystroke via sampleArcPolyline() (see arcFromSagitta.js - segment count
// is chosen from the arc's own radius/sweep against
// CURVE_SAMPLING_TOLERANCE_M, a fixed internal chord-error budget - NOT the
// "Maksimal pilhøjde" editor setting, and NOT preserved from whatever the
// range had before - see CURVE_SAMPLING_TOLERANCE_M's own doc comment for
// why the two are deliberately decoupled), spliced back into the ORIGINAL
// surrounding points (captured once at
// dialog-open - the part outside [startIndex..endIndex] never changes
// during this dialog). Because the vertex COUNT changes on nearly every
// preview step, the generic reconcileArcRanges() rule in
// DigitizationModule._wireFeatureLifecycle() (task §9: "any vertex-count
// change clears every stored range") fires on every one of THIS dialog's
// own setPoints() calls too - exactly as it would for a manual insert/
// delete elsewhere, since it has no way to distinguish the two. This
// dialog's OWN correction (resampleArcRange() - see arcRangeStore.js's own
// doc comment) runs immediately AFTER each setPoints() call and
// unconditionally overwrites whatever the generic rule just cleared with
// the PRECISE, correctly-reindexed set of ranges - the edited range's own
// fresh record, every sibling range on the same feature shifted by exactly
// the same delta this resample introduced (task §4: "hvis feature har
// flere arc-ranges, skal ændring af én bue ikke ødelægge de øvrige").
// `currentRange` tracks this dialog's own range's bounds as they exist
// RIGHT NOW (starts at the caller-supplied span, which for "Rediger bue…"
// already matches the existing stored entry) - resampleArcRange() needs
// this to find (and replace) the correct sibling in the PRE-setPoints()
// snapshot on every step.
//
// Annuller/ESC restores the ORIGINAL geometry (including its ORIGINAL
// vertex COUNT - `originalPoints` was captured before any resampling) AND
// the original arc-range metadata exactly (both snapshotted once at
// dialog-open time) - "Annuller" must undo everything the live preview did,
// not just the last step.
export function openArcDialog(dm, visual, {startIndex, endIndex, initialSagittaCm = 0, existingRange = null, initialPlane = null} = {}){
	return new Promise((resolve) => {
		const feature = dm.getFeature(visual);
		if(!feature){ resolve(false); return; }

		const originalPoints = toPlainPoints(visual);
		const originalArcRanges = getArcRanges(feature);
		const chordStart = originalPoints[startIndex];
		const chordEnd = originalPoints[endIndex];

		// `initialPlane` (task DEL D: direct "Bue" creation tool) lets the
		// caller supply an EXPLICIT plane derived from a third clicked point,
		// bypassing determineArcPlane()'s own interior-vertex/vertical-
		// fallback heuristic entirely - appropriate here because a brand-new
		// 2-vertex chord has no interior geometry to derive a bulge signal
		// from at all, and the user has already explicitly chosen a plane by
		// clicking that third point (task: "Undgå kameraafhængigt eller
		// tilfældigt bueplan" - an explicit user choice is the most stable
		// possible source, more so than either fallback).
		const interiorPoints = originalPoints.slice(startIndex + 1, endIndex);
		const plane = initialPlane
			? initialPlane
			: existingRange
				? planeFromArcRange(originalPoints, existingRange)
				: determineArcPlane(chordStart, chordEnd, interiorPoints);

		if(!plane){
			showGeometryWarning(dm, t("Bue fra pilhøjde"), t("Intervallets to endepunkter er sammenfaldende - der kan ikke beregnes en bue."));
			resolve(false);
			return;
		}

		let sagittaCm = initialSagittaCm;
		let currentRange = {startIndex, endIndex};
		let done = false;

		const applyPreview = () => {
			const sagittaM = sagittaCm / 100;
			const sample = sampleArcPolyline(chordStart, chordEnd, sagittaM, plane, {toleranceM: CURVE_SAMPLING_TOLERANCE_M});
			const newFullPoints = [
				...originalPoints.slice(0, startIndex),
				...sample.points,
				...originalPoints.slice(endIndex + 1),
			];

			// snapshot BEFORE setPoints() - that call synchronously runs the
			// generic reconciliation rule, which would already have cleared
			// this by the time we read it afterwards
			const beforeRanges = getArcRanges(feature);
			const newRange = {
				startIndex,
				endIndex: startIndex + sample.points.length - 1,
				sagittaM,
				planeU: [plane.u.x, plane.u.y, plane.u.z],
			};

			visual.setPoints(toVector3Array(newFullPoints));

			const ranges = resampleArcRange(beforeRanges, currentRange, newRange);
			dm.featureManager.setDomainData(feature.id, "arc", {ranges});
			currentRange = {startIndex: newRange.startIndex, endIndex: newRange.endIndex};
			return sample;
		};

		const {overlay, box} = createDialogOverlay(dm, 300);

		fillDialog(box, {
			title: `${existingRange ? t("Rediger bue") : t("Bue fra pilhøjde")}`,
			description: t(`Positiv pilhøjde buer til den ene side, negativ til den anden. Ændringen vises direkte i 3D, mens du skriver.`),
			body: `<label class="pvp-label">${t("Pilhøjde [cm]")}</label>
				<input class="pvp-input" type="number" step="any" value="${sagittaCm}"
				 />
				<div class="pvp-note" data-role="radius"></div>`,
			actions: pvpButton('flip', t("Vend bue"), 'start') + pvpButton('cancel', t("Annuller")) + pvpButton('commit', t("Godkend"), 'primary'),
		});

		const input = box.querySelector('input');
		const radiusEl = box.querySelector('[data-role="radius"]');
		input.focus();
		input.select();

		const updateRadiusReadout = (sample) => {
			radiusEl.textContent = sample.radiusM != null
				? t("Radius: {0} · {1} segmenter", formatLength(sample.radiusM), sample.segmentCount)
				: t("Pilhøjde 0 - ret linje (ingen bue)");
		};
		updateRadiusReadout(applyPreview());

		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};

		const finish = (committed) => {
			if(done) return;
			done = true;
			cleanup();
			if(committed){
				dm.clearMultiSelection();
			}else{
				// exact restore - both the geometry AND the arc metadata as they
				// were the instant this dialog opened
				visual.setPoints(toVector3Array(originalPoints));
				dm.featureManager.setDomainData(feature.id, "arc", {ranges: originalArcRanges});
			}
			resolve(committed);
		};

		input.addEventListener('input', () => {
			const parsed = parseFloat(input.value);
			sagittaCm = isFinite(parsed) ? parsed : 0;
			updateRadiusReadout(applyPreview());
		});
		box.querySelector('[data-action="flip"]').addEventListener('click', () => {
			sagittaCm = -sagittaCm;
			input.value = sagittaCm;
			updateRadiusReadout(applyPreview());
		});
		box.querySelector('[data-action="commit"]').addEventListener('click', () => finish(true));
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(false));
		const onCancelEvent = () => finish(false);
		box.addEventListener('keydown', (e) => {
			if(e.key === 'Escape'){ e.stopPropagation(); finish(false); }
			if(e.key === 'Enter'){ e.preventDefault(); finish(true); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

async function runArcFromSagitta(dm, visual, range){
	const points = toPlainPoints(visual);
	const [start, end] = resolveRange(range, points);
	await openArcDialog(dm, visual, {startIndex: start, endIndex: end});
}

async function runEditArc(dm, visual, arcRange){
	await openArcDialog(dm, visual, {
		startIndex: arcRange.startIndex,
		endIndex: arcRange.endIndex,
		initialSagittaCm: arcRange.sagittaM * 100,
		existingRange: arcRange,
	});
}

// One-click flip (task's own "acceptable" alternative to a signed sagitta
// sign, offered alongside it) - bulges to the OPPOSITE side, same
// magnitude, without opening the dialog. Same commit shape as the dialog's
// own per-preview step (resample -> setPoints() -> resampleArcRange() ->
// setDomainData()), just run once, immediately. |sagitta| is unchanged by a
// flip, so the segment count from computeArcSegmentCount() is identical too
// (R and |sweep| both depend only on |sagitta|) - this still goes through
// the general resample path rather than assuming that, so it stays correct
// even if that ever changes.
function runFlipArc(dm, visual, arcRange){
	const feature = dm.getFeature(visual);
	if(!feature) return;

	const points = toPlainPoints(visual);
	const chordStart = points[arcRange.startIndex];
	const chordEnd = points[arcRange.endIndex];
	const plane = planeFromArcRange(points, arcRange);
	const flippedSagittaM = -arcRange.sagittaM;

	const sample = sampleArcPolyline(chordStart, chordEnd, flippedSagittaM, plane, {toleranceM: CURVE_SAMPLING_TOLERANCE_M});
	const newFullPoints = [
		...points.slice(0, arcRange.startIndex),
		...sample.points,
		...points.slice(arcRange.endIndex + 1),
	];

	const beforeRanges = getArcRanges(feature);
	const newRange = {
		startIndex: arcRange.startIndex,
		endIndex: arcRange.startIndex + sample.points.length - 1,
		sagittaM: flippedSagittaM,
		planeU: arcRange.planeU,
	};

	visual.setPoints(toVector3Array(newFullPoints));
	const ranges = resampleArcRange(beforeRanges, arcRange, newRange);
	dm.featureManager.setDomainData(feature.id, "arc", {ranges});
	dm.clearMultiSelection();
}

// Removes the parametric arc-definition only (task §8) - the current XYZ
// coordinates are left exactly as they are; the range becomes ordinary,
// freely-editable LineString vertices again.
function runDissolveArc(dm, visual, arcRange){
	const feature = dm.getFeature(visual);
	if(!feature) return;

	const ranges = removeArcRange(feature, arcRange.startIndex, arcRange.endIndex);
	dm.featureManager.setDomainData(feature.id, "arc", {ranges});
}

function startSegmentSelection(dm, visual, firstHit){
	const domElement = dm.viewer.renderer.domElement;
	dm._showInstruction(t("Vælg sammenhængende del"), t("Venstreklik for at vælge startposition på vektoren."));

	let finished = false;
	let startIndex = null;

	const cleanup = () => {
		domElement.removeEventListener('mouseup', onClick, false);
		dm.viewer.removeEventListener('cancel_insertions', onCancel);
		dm._hideToolHint();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		clearHighlight(visual);
	};

	const finishSelection = (endIndex, clientX, clientY) => {
		finished = true;
		cleanup();

		const lo = Math.min(startIndex, endIndex);
		const hi = Math.max(startIndex, endIndex);
		highlightRange(visual, lo, hi);

		showRangeOperationsMenu(dm, visual, {start: lo, end: hi}, clientX, clientY, () => clearHighlight(visual));
	};

	const onClick = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard() || e.button !== THREE.MOUSE.LEFT) return;

		const hit = dm._findHoveredSegment(e);
		if(!hit || hit.visual !== visual) return;

		const vertexIndex = hit.t < 0.5 ? hit.segmentIndex : hit.segmentIndex + 1;

		if(startIndex === null){
			startIndex = vertexIndex;
			dm._showInstruction(t("Vælg sammenhængende del"), t("Venstreklik for at vælge slutposition på vektoren. ESC annullerer."));
			return;
		}

		finishSelection(vertexIndex, e.clientX, e.clientY);
	};

	domElement.addEventListener('mouseup', onClick, false);
	dm.viewer.addEventListener('cancel_insertions', onCancel);

	// the menu-opening click that led here was itself a Shift+Right-click,
	// not a plain left-click, so `firstHit` is only used to pre-fill nothing
	// here - the user always explicitly places both ends, per task section 6
	// ("ingen segmentnumre skal indtastes manuelt")
	void firstHit;
}

// Integrations-laase (trin G1) og integrationens handlinger for en visual: begge kommer fra featurens egen binding via
// DigitizationModule (adapteren er eneste kilde, samme liste som Registreringer-træet). Ubundne Features: ingen laase,
// ingen handlinger.
function integrationContext(dm, visual){
	const feature = dm.getFeature(visual);
	if(!feature) return {locks: {geometryLocked: false, structureLocked: false}, actions: [], handlers: {}};

	const actions = dm.getFeatureActions(feature.id) || [];
	const handlers = {};
	for(const action of actions) handlers["integration:" + action.id] = () => dm.runFeatureAction(feature.id, action.id);
	return {locks: dm.getEditingLocks(feature), actions, handlers};
}

function showRangeOperationsMenu(dm, visual, range, clientX, clientY, onClosed){
	const segmentCount = range.end - range.start;
	const caps = getEditingCapabilities({geometryType: visual.geometryType, selectionType: "range", range, ...integrationContext(dm, visual).locks});

	// Structure/terminology/grouping come entirely from menuPlan.js (pure,
	// unit-tested); here we only bind each action id to its closure. An
	// operation that doesn't apply to this range never reaches the plan.
	const menu = bindPlan(planRangeMenu({caps, segmentCount}), {
		straighten2D: () => runStraighten(dm, visual, "2D", range),
		straighten3D: () => runStraighten(dm, visual, "3D", range),
		arcFromSagitta: () => runArcFromSagitta(dm, visual, range),
		interpolateZ: () => runInterpolateZ(dm, visual, range),
		setSlope: () => runSetSlope(dm, visual, range),
		simplify: () => runSimplify(dm, visual, range),
		orthogonalize: () => runOrthogonalize(dm, visual, range),
		distribute: () => runDistributeEvenly(dm, visual, range),
		reshape: () => runReshapeRange(dm, visual, range),
		moveLineZ: () => runMoveLineZ(dm, visual, "range", range.start, range.end),
		fixedAngle: () => runFixedAngle(dm, visual, range.start + 1),
	});

	if(menu.groups.length === 0){
		// nothing applicable to this particular range - don't open an empty
		// menu, just clear the transient selection highlight it would have shown
		onClosed();
		return;
	}

	showMenuWithHighlightClear(clientX, clientY, menu, onClosed);
}

// Selection-model audit finding: a box-selected (see BoxSelection.js)
// contiguous partial vertex range on a single feature is, geometrically,
// EXACTLY the same thing "Vælg sammenhængende del"'s own two-click flow
// produces - the same {visual, range} this file already knows how to
// highlight and act on. Without this, the SAME underlying selection (a
// contiguous mid-line range) would offer DIFFERENT operations depending on
// which of the two ways the user reached it - precisely the "samme
// geometri, forskellig menu afhængig af selection-vej" inconsistency a
// previous revision of this file already fixed once for feature/range (see
// EditingCapabilities.js's own "Unification note"). One shared entry point,
// reusing highlightRange()/showRangeOperationsMenu()/clearHighlight()
// unchanged, rather than a second implementation of range highlight+menu.
export function openRangeMenuForVisual(dm, visual, range, clientX, clientY){
	highlightRange(visual, range.start, range.end);
	showRangeOperationsMenu(dm, visual, range, clientX, clientY, () => clearHighlight(visual));
}

// ── entry point ──────────────────────────────────────────────────────────

// Called from DigitizationModule's Shift+Right-click handler once a segment
// (not a vertex - those take priority, see _findHoveredVertex()) is hit.
// Segment-scoped operations only - whole-feature operations (Ret ud, Vend
// retning, Slet objekt, ...) belong to openFeatureMenu() instead, shown only
// when the selection actually IS the whole feature (task section 5: a
// segment-menu must never carry whole-feature operations).
//
// Returns true if a menu was actually shown (false when the selection has
// zero applicable operations - e.g. a Point, which never reaches here in
// practice since it has no segments) so the caller knows whether to treat
// the click as consumed.
export function openVectorOperationsMenu(dm, visual, hit, clientX, clientY){
	// Resolved once up front (rather than inside EditingCapabilities.js
	// itself, which stays DOM/FeatureManager-free - see its own doc comment)
	// so both the capability gate AND the bound handlers below share the
	// exact same arc-range record.
	const feature = dm.getFeature(visual);
	const arcRange = feature ? findArcRangeForSegment(feature, hit.segmentIndex) : null;

	const caps = getEditingCapabilities({
		objectKind: resolveObjectKind(dm, visual),
		geometryType: visual.geometryType,
		selectionType: "segment",
		segmentIndex: hit.segmentIndex,
		segmentT: hit.t,
		vertexCount: visual.points.length,
		arcRange,

		vertexCount: visual.points.length,
		...integrationContext(dm, visual).locks,
	});

	// Structure/grouping/terminology from menuPlan.js (pure, unit-tested);
	// here we only bind ids to closures. "Vælg hele objektet" routes to
	// openFeatureMenu() - the ONE whole-object menu builder (see its own doc
	// comment) - never a synthetic full-span range.
	const menu = bindPlan(planSegmentMenu({caps}), {
		insert: () => runInsertPoint(dm, visual, hit),
		split: () => runSplitLine(dm, visual, hit.segmentIndex, hit.t),
		trimBridge: () => runTrimAndBridge(dm, visual, hit.segmentIndex),
		moveLineZ: () => runMoveLineZ(dm, visual, "segment", hit.segmentIndex, hit.segmentIndex + 1),
		fixedAngle: () => runFixedAngle(dm, visual, resolveFixedAngleVertex(visual, hit)),
		parallel: () => runAlignToReference(dm, visual, hit.segmentIndex, "parallel"),
		perpendicular: () => runAlignToReference(dm, visual, hit.segmentIndex, "perpendicular"),
		intersect: () => runIntersectInsert(dm, visual, hit),
		selectRange: () => startSegmentSelection(dm, visual, hit),
		selectWhole: () => {
			dm.selectVisual(visual);
			openFeatureMenu(dm, visual, clientX, clientY);
		},
		editArc: () => runEditArc(dm, visual, arcRange),
		flipArc: () => runFlipArc(dm, visual, arcRange),
		dissolveArc: () => runDissolveArc(dm, visual, arcRange),
	});

	if(menu.groups.length === 0) return false;

	// discreet single-edge highlight (task section 11: "Segment -> fremhæv
	// segment diskret") - same COLOR_HIGHLIGHT/clear-on-close convention
	// Vælg sammenhængende del's own range highlight already uses; reuses
	// highlightRange() for exactly one edge rather than a new helper.
	highlightRange(visual, hit.segmentIndex, hit.segmentIndex + 1);
	showMenuWithHighlightClear(clientX, clientY, menu, () => clearHighlight(visual));
	return true;
}

// Whole-object menu (task section 6, generalised platform-wide) - shown
// when Shift+Right-click hits neither a vertex, segment, fill nor a
// standalone whole-object registration (Rør) directly (see
// DigitizationModule's mouseup handler and _findHoveredWholeObject()).
// Never carries segment-scoped operations (Indsæt punkt, Parallel/Vinkelret
// på reference, Fast vinkel). Title and available operations both depend on
// `objectKind` (see resolveObjectKind()/EditingCapabilities.js's own header
// comment) - Punkt/Linje/Polygon/Rør/Tværprofil all get their own, honestly
// distinct menu instead of one generic "Vektor" label.
export function openFeatureMenu(dm, visual, clientX, clientY){
	const objectKind = resolveObjectKind(dm, visual);
	const vertexCount = visual.points ? visual.points.length : 0;
	// Resolved once up front (same "the pure capability model stays
	// DOM/FeatureManager-free" reasoning as arcRange for the segment menu -
	// see EditingCapabilities.js's own doc comment) - "circle" | "rectangle" |
	// null, from the selected Polygon's own domainData.
	const feature = dm.getFeature(visual);
	const parametricShapeKind = feature && feature.domainData.circle ? "circle"
		: feature && feature.domainData.rectangle ? "rectangle"
			: null;
	const integration = integrationContext(dm, visual);
	const caps = getEditingCapabilities({objectKind, geometryType: visual.geometryType, selectionType: "feature", vertexCount, parametricShapeKind, ...integration.locks});

	const isPolygon = visual.geometryType === "Polygon";

	// Structure/grouping/terminology/ordering are all decided by menuPlan.js
	// (pure, unit-tested: GEOMETRI → POSITIONERING → STRUKTUR → RAPPORT, then
	// Slet objekt behind its own divider). Here we only bind ids to closures.
	// Forenkle/Ortogonaliser dispatch to the closed-ring variant for a
	// Polygon.
	const menu = bindPlan(planFeatureMenu({objectKind, caps, parametricShapeKind, integrationActions: integration.actions}), {
		...integration.handlers,
		straighten2D: () => runStraighten(dm, visual, "2D"),
		straighten3D: () => runStraighten(dm, visual, "3D"),
		arcFromSagitta: () => runArcFromSagitta(dm, visual, null),
		interpolateZ: () => runInterpolateZ(dm, visual, null),
		setSlope: () => runSetSlope(dm, visual, null),
		simplify: () => (isPolygon ? runSimplifyPolygon(dm, visual) : runSimplify(dm, visual)),
		orthogonalize: () => (isPolygon ? runOrthogonalizePolygon(dm, visual) : runOrthogonalize(dm, visual)),
		// canFixedAngle is only granted here when the whole line spans exactly
		// 2 segments (3 vertices) - vertex index 1 is then the one interior
		// vertex, same as the segment menu's resolveFixedAngleVertex().
		fixedAngle: () => runFixedAngle(dm, visual, 1),
		distribute: () => runDistributeEvenly(dm, visual),
		moveLineZ: () => runMoveLineZ(dm, visual, "line"),
		snapAll: () => runSnapAllToPointCloud(dm, visual),
		reverse: () => runReverse(dm, visual),
		join: () => runMergeLines(dm, visual),
		trimBridge: () => runTrimAndBridge(dm, visual, null),
		report: () => showCrossSectionReportForFeature(dm, visual),
		editParametricShape: () => (parametricShapeKind === "rectangle" ? runEditRectangle(dm, visual) : runEditCircle(dm, visual)),
		dissolveParametricShape: () => (parametricShapeKind === "rectangle" ? runDissolveRectangle(dm, visual) : runDissolveCircle(dm, visual)),
		delete: () => {
			if(feature) dm.requestRemoveFeature(feature.id);
		},
	});

	if(menu.groups.length === 0) return false;

	showGroupedContextMenu(clientX, clientY, menu);
	return true;
}

// Vertex menu (task section 4) - Shift+Right-click on a vertex, always
// vertex-only (never segment/whole-object operations, any geometry type).
export function openVertexMenu(dm, visual, vertexIndex, clientX, clientY){
	const integration = integrationContext(dm, visual);
	const caps = getEditingCapabilities({
		objectKind: resolveObjectKind(dm, visual),
		geometryType: visual.geometryType,
		selectionType: "vertex",
		vertexIndex,
		vertexCount: visual.points.length,
		...integration.locks,
	});

	// Basic Move / Snap operations act on the selected vertex ONLY. Geometric
	// coincidence with another feature's endpoint is NOT a scope choice here
	// (stabilisation task §3) - it is surfaced by the Topology-QA panel and,
	// after a move that broke one, by checkVertexMoveConnectionWarning()'s toast.
	const plan = planVertexMenu({
		geometryType: visual.geometryType,
		vertexIndex,
		vertexCount: visual.points.length,
		caps,
		deleteRemovesWholeFeature: visual.points.length <= visual.minVertices,
		integrationActions: integration.actions,
	});

	const menu = bindPlan(plan, {
		...integration.handlers,
		move: () => runMovePoint(dm, visual, vertexIndex),
		moveZ: () => runMovePointZ(dm, visual, vertexIndex),
		setAbsoluteZ: () => runSetAbsoluteZ(dm, visual, vertexIndex),
		snapCloud: () => runSnapToPointCloud(dm, visual, vertexIndex),
		snapPoint: () => runSnapToOtherPoint(dm, visual, vertexIndex),
		split: () => runSplitLine(dm, visual, vertexIndex - 1, 1),
		continueLine: () => runContinueLine(dm, visual, vertexIndex),
		extend: () => runExtendToReference(dm, visual, vertexIndex),
		extendSegment: () => runExtendEndSegment(dm, visual, vertexIndex),
		trim: () => runTrimToReference(dm, visual, vertexIndex),
		connect: () => runConnectToEndpoint(dm, visual, vertexIndex),
		delete: () => {
			// geometri-låst (integrationsbundet) objekt: kun HELE objektet kan slettes - aldrig et enkelt punkt lokalt
			if(!caps.canDeleteVertex){
				const lockedFeature = dm.getFeature(visual);
				if(lockedFeature && caps.canDeleteFeature) dm.requestRemoveFeature(lockedFeature.id);
				return;
			}
			const removed = visual.removeVertex(vertexIndex);
			if(!removed){
				const feature = dm.getFeature(visual);
				if(feature) dm.requestRemoveFeature(feature.id);
			}
		},
	});

	if(menu.groups.length === 0) return false;

	// Selection/highlight audit finding: mark WHICH vertex the menu concerns
	// (the sphere's own hover emissive clears the instant the cursor leaves
	// it toward the menu). Same highlight-on-open/clear-on-close convention as
	// the segment/range menus, applied via FeatureVisual.setVertexHighlight().
	if(visual.setVertexHighlight) visual.setVertexHighlight(vertexIndex, true);
	showMenuWithHighlightClear(clientX, clientY, menu, () => {
		if(visual.setVertexHighlight) visual.setVertexHighlight(vertexIndex, false);
	});
	return true;
}

// ── Multi-selection (box-selection result - see BoxSelection.js) ─────────
//
// Deliberately minimal (task section 10: "Context-menuen skal kun vise
// operationer, der giver mening for det aktuelle selection-set"): there is
// no existing operation in this codebase that generalises to an arbitrary
// N-element set beyond deletion (no bulk Ret ud/Forenkle/etc across
// unrelated vertices/features would even mean the same thing per element),
// so offering only "Slet valgte.../Ryd valgte" is an honest reflection of
// what actually applies, not a shortcut.

// Vertex-scope deletion groups entries by feature (a box-selection can span
// several features at once) and deletes highest-index-first per feature so
// removing one vertex never shifts the index of another not-yet-removed one
// in the same feature - same underlying FeatureVisual.removeVertex() the
// single-vertex "Slet punkt" menu action already uses, same minVertices-
// floor fallback (remove the whole feature once a group's remaining
// vertices hit the floor - mirrors openVertexMenu's own dynamic "Slet
// objekt" vs. "Slet punkt" logic, just applied per group instead of once).
function runDeleteMultiSelectionVertices(dm, entries){
	const byVisual = new Map();
	for(const entry of entries){
		if(!byVisual.has(entry.visual)) byVisual.set(entry.visual, []);
		byVisual.get(entry.visual).push(entry.vertexIndex);
	}

	for(const [visual, indices] of byVisual){
		const feature = dm.getFeature(visual);
		if(!feature) continue;
		// integrationsbundet (geometri-låst) objekt: ingen lokal punkt-sletning (kun hele objektet via "Slet valgte objekter")
		if(dm.getEditingLocks(feature).geometryLocked) continue;

		const sortedHighestFirst = indices.slice().sort((a, b) => b - a);
		for(const index of sortedHighestFirst){
			const removed = visual.removeVertex(index);
			if(!removed){
				dm.requestRemoveFeature(feature.id);
				break;
			}
		}
	}

	// removeVertex() (unlike applyFeatureGeometry()/removeFeature()) does
	// not itself clear multiSelection - it's called directly here, the same
	// way the single-vertex "Slet punkt" action already calls it directly -
	// so this is cleared explicitly once the whole batch is done.
	dm.clearMultiSelection();
}

// "Snap til dette punkt" (kun ved flervalgte PUNKTER): alle valgte vertices flyttes til det vertex, menuen blev åbnet på.
// Samme flytning som "Snap til andet punkt" (movePoint() + applyFeatureGeometry(), målets fulde XYZ), men for hele mængden i ét
// hug: ét geometri-skift pr. berørt feature. Målvertex'et flyttes aldrig; integrationsbundne (geometri-låste) objekter springes
// over. Målet læses fra dets rigtige geometri (aldrig et nyt punktsky-pick).
function runSnapMultiSelectionToVertex(dm, entries, targetHit){
	const targetFeature = dm.getFeature(targetHit.visual);
	const targetPoint = targetHit.visual.points[targetHit.index];
	if(!targetFeature || !targetPoint) return;

	const targetPosition = {x: targetPoint.position.x, y: targetPoint.position.y, z: targetPoint.position.z};
	const candidates = [];
	for(const entry of entries){
		const feature = dm.getFeature(entry.visual);
		if(feature) candidates.push({featureId: feature.id, vertexIndex: entry.vertexIndex, visual: entry.visual});
	}

	const moves = planSnapSelectionToTarget(candidates, {featureId: targetFeature.id, vertexIndex: targetHit.index}, targetPosition);

	const byVisual = new Map();
	for(const move of moves){
		if(!byVisual.has(move.visual)) byVisual.set(move.visual, []);
		byVisual.get(move.visual).push(move);
	}

	let skippedLocked = 0;
	for(const [visual, visualMoves] of byVisual){
		const feature = dm.getFeature(visual);
		if(!feature) continue;
		if(dm.getEditingLocks(feature).geometryLocked){
			skippedLocked += visualMoves.length;
			continue;
		}

		let points = toPlainPoints(visual);
		for(const move of visualMoves){
			points = movePoint(points, move.vertexIndex, move.position).points;
		}
		dm.applyFeatureGeometry(visual, toVector3Array(points)); // rydder også flervalget
	}

	dm.clearMultiSelection();

	if(skippedLocked > 0){
		showGeometryWarning(dm, t("Snap til dette punkt"),
			skippedLocked === 1
				? t("{0} punkt blev ikke flyttet, fordi objektet er bundet til en integration (geometrien er låst).", skippedLocked)
				: t("{0} punkter blev ikke flyttet, fordi objektet er bundet til en integration (geometrien er låst).", skippedLocked));
	}
}

function runDeleteMultiSelectionFeatures(dm, entries){
	for(const entry of entries){
		const feature = dm.getFeature(entry.visual);
		if(feature) dm.requestRemoveFeature(feature.id); // already clears multiSelection itself
	}
}

// Shown instead of the ordinary vertex→segment→fill→whole-object menu chain
// whenever DigitizationModule.multiSelection has 2+ entries (see
// DigitizationModule.js's Shift+Right-click handler - checked first, before
// any hit-test). Returns false only if multiSelection has somehow become
// empty/invalid by the time this runs (defensive - BoxSelection._finish()
// never sets it with fewer than 2 entries).
export function openMultiSelectionMenu(dm, clientX, clientY){
	const multi = dm.multiSelection;
	if(!multi || multi.entries.length < 2) return false;

	const isVertexScope = multi.scope === "vertex";
	const count = multi.entries.length;

	// Same "Slet… sits in its own trailing, unlabelled group, separated by a
	// divider from every other action" principle as menuPlan.js's assemble()
	// uses for the vertex/feature menus (planVertexMenu/planFeatureMenu) -
	// applied by hand here since this menu is small enough it was never
	// built through that planner, but the visual result must still match.
	// "Snap til dette punkt": kun ved flervalgte punkter, og kun når menuen blev åbnet på et vertex (det er det punkt, alle
	// valgte snapper til) - ellers er der intet "dette punkt" at snappe til.
	const targetHit = isVertexScope ? dm._findHoveredVertex() : null;
	const groups = [];
	if(targetHit){
		groups.push({items: [{label: t("Snap til dette punkt"), onSelect: () => runSnapMultiSelectionToVertex(dm, multi.entries.slice(), targetHit)}]});
	}
	groups.push(
		{items: [{label: t("Ryd valgte"), onSelect: () => dm.clearMultiSelection()}]},
		{items: [{
			label: isVertexScope ? t("Slet valgte punkter ({0})", count) : t("Slet valgte objekter ({0})", count),
			onSelect: () => {
				if(isVertexScope) runDeleteMultiSelectionVertices(dm, multi.entries);
				else runDeleteMultiSelectionFeatures(dm, multi.entries);
			},
		}]},
	);

	showGroupedContextMenu(clientX, clientY, {
		title: isVertexScope ? t("{0} punkter valgt", count) : t("{0} objekter valgt", count),
		groups,
	});
	return true;
}
